/**
 * ManagerAttestations.js
 *
 * WHAT: Attestation review interface for managers to certify, modify, or revoke database user access.
 * WHY:  During the ATTESTATION phase of a campaign, managers must review every database account
 *       assigned to them and make retain/modify/remove decisions. This component provides the
 *       primary work surface for that review, including bulk actions, delegation to other attesters,
 *       risk detail drilldowns, privilege modification, and progress tracking against deadlines.
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getPrivilegeLevelStyle } from '../utils/privilegeLevels';
import API_CONFIG from '../config/api.config';
import { WideModal, StatusRibbon } from './common';
import UserAccessReportTab from './ManagerAttestations/UserAccessReportTab';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Manager-facing attestation table with per-account decision controls and bulk operations.
 * WHY:  Regulatory frameworks (SOX, PCI-DSS, etc.) require periodic certification of database
 *       access. This component is where managers fulfill that obligation by reviewing each
 *       assigned account, recording decisions, and optionally delegating to another attester.
 */
const ManagerAttestations = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  // Structured ribbon state — used when a single flat string cannot convey
  // partial/skipped outcomes. Shape: { tone, title, details, autoDismissMs }.
  // When set, it takes precedence over the legacy successMessage string.
  const [statusRibbon, setStatusRibbon] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [stats, setStats] = useState({
    total_users: 0,
    total_campaigns: 0,
    pending_decisions: 0,
    completed_decisions: 0,
    completed_retained: 0,
    completed_modified: 0,
    completed_removed: 0
  });
  const [advancedStats, setAdvancedStats] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({
    campaign: [],
    database: [],
    db_username: [],
    employee: [],
    application: [],
    attester: [],
    privilege_level: [],
    risk_level: [],
    attestation_decision: []
  });
  const [filterOptions, setFilterOptions] = useState({
    campaigns: [],
    databases: [],
    db_usernames: [],
    employees: [],
    applications: [],
    attesters: [],
    privilege_levels: [],
    risk_levels: [],
    attestation_decisions: []
  });
  const [campaignDetails, setCampaignDetails] = useState({}); // Store campaign details including attestation_deadline
  // WHAT: Per-technology enabled-tier list from the Privilege Matrix.
  // WHY:  Sidebar privilege groups, table filter options, and Modify dropdowns must reflect the
  //       matrix (e.g. MSSQL has READ/WRITE/ADMIN/NONE enabled; READ_ELEVATED/SUPER_ADMIN disabled).
  //       Shape: { mssql: [{tier_name, tier_value, sort_order}, ...], oracle: [...] }
  const [matrixTiersByTech, setMatrixTiersByTech] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [viewFilter, setViewFilter] = useState('pending'); // 'all' or 'pending'
  const [activeTab, setActiveTab] = useState('attestation'); // 'attestation' | 'accessReport'
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [showUserDelegateModal, setShowUserDelegateModal] = useState(false);
  const [usersToDelegateKeys, setUsersToDelegateKeys] = useState([]);
  const [delegateAttester, setDelegateAttester] = useState({ id: '', name: '', email: '' });
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [delegateSearchTerm, setDelegateSearchTerm] = useState('');

  // Privilege details modal state
  const [showPrivilegeDetailModal, setShowPrivilegeDetailModal] = useState(false);
  const [privilegeDetailLoading, setPrivilegeDetailLoading] = useState(false);
  const [privilegeDetailData, setPrivilegeDetailData] = useState(null);

  // Modify privilege modal state
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyModalUser, setModifyModalUser] = useState(null);
  const [targetPrivilegeLevel, setTargetPrivilegeLevel] = useState('');

  // System account warning modal state (for Oracle maintained, Cassandra default, etc.)
  const [showSystemAccountWarningModal, setShowSystemAccountWarningModal] = useState(false);
  const [pendingSystemAccountDecision, setPendingSystemAccountDecision] = useState(null);
  const [modifyComment, setModifyComment] = useState('');

  // Self-attestation prevention modal state
  const [showSelfAttestModal, setShowSelfAttestModal] = useState(false);
  const [selfAttestUser, setSelfAttestUser] = useState(null);

  // Risk detail modal state
  const [showRiskDetailModal, setShowRiskDetailModal] = useState(false);
  const [selectedUserForRiskDetail, setSelectedUserForRiskDetail] = useState(null);

  // Risk Levels explanation modal state
  const [showRiskLevelsModal, setShowRiskLevelsModal] = useState(false);

  // All users attested notification modal state
  const [showAllAttestedModal, setShowAllAttestedModal] = useState(false);
  const [allAttestedModalMessage, setAllAttestedModalMessage] = useState('');
  const [allAttestedModalType, setAllAttestedModalType] = useState('attested'); // 'attested' or 'closed'

  // Clear attestation modal state (for already attested users)
  const [showClearAttestationModal, setShowClearAttestationModal] = useState(false);

  // Bulk Modify modal state
  const [showBulkModifyModal, setShowBulkModifyModal] = useState(false);
  const [bulkModifyUsers, setBulkModifyUsers] = useState([]);
  const [bulkTargetPrivilegeLevel, setBulkTargetPrivilegeLevel] = useState('');
  const [bulkModifyComment, setBulkModifyComment] = useState('');
  const [showMixedPrivilegeModal, setShowMixedPrivilegeModal] = useState(false);
  const [mixedPrivilegeLevels, setMixedPrivilegeLevels] = useState([]);

  // Floating action bar position state (for dragging)
  const [floatingBarPosition, setFloatingBarPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Group attestation state
  const [showGroupMembersModal, setShowGroupMembersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);
  const [attestingMemberId, setAttestingMemberId] = useState(null);

  // Hierarchical sidebar navigation state
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedPrivilegeLevel, setSelectedPrivilegeLevel] = useState(null);
  const [selectedRiskLevel, setSelectedRiskLevel] = useState(null);
  const [selectedDatabases, setSelectedDatabases] = useState([]);  // Changed to array for multi-select

  // Guide Me state
  const [guideMode, setGuideMode] = useState(false);
  const [guideSubStep, setGuideSubStep] = useState(null); // For sub-steps within step 4

  // Guide Me step definitions for attestation workflow
  const guideSteps = [
    {
      step: 1,
      title: 'Select Campaign',
      description: 'Choose an active campaign from the sidebar. Campaigns contain users grouped by database and risk level.',
      isComplete: () => selectedCampaign !== null,
      target: 'campaign'
    },
    {
      step: 2,
      title: 'Select Risk Level',
      description: 'Click a risk category (Critical, High, Medium, Low) to see databases at that privilege level.',
      isComplete: () => selectedPrivilegeLevel !== null,
      target: 'privilegeLevel'
    },
    {
      step: 3,
      title: 'Select Databases',
      description: 'Check one or more databases to load their users for review. Users appear in the right panel.',
      isComplete: () => selectedDatabases.length > 0,
      target: 'databases'
    },
    {
      step: 4,
      title: 'Review Users',
      description: 'Examine each user\'s details including their privileges, account status, and last activity date.',
      isComplete: () => guideSubStep !== null && guideSubStep > 4,
      target: 'reviewUsers'
    },
    {
      step: 5,
      title: 'Make Decisions',
      description: 'For each user, click Retain (approve access), Modify (change privilege level), or Remove (revoke access).',
      isComplete: () => guideSubStep !== null && guideSubStep > 5,
      target: 'makeDecisions'
    },
    {
      step: 6,
      title: 'Delegate Reviews',
      description: 'Optional: Use "Bulk Delegation" to transfer pending attestations to another manager, or select specific users to delegate.',
      isComplete: () => guideSubStep !== null && guideSubStep > 6,
      target: 'delegate'
    },
    {
      step: 7,
      title: 'Track Progress',
      description: 'Monitor completion using the progress bar. Campaign completes when all in-scope users have decisions.',
      isComplete: () => false,
      target: 'trackProgress'
    }
  ];

  // Get current guide step based on progress
  const getCurrentGuideStep = () => {
    // Steps 1-3 auto-progress based on selections
    if (selectedCampaign === null) return 1;
    if (selectedPrivilegeLevel === null) return 2;
    if (selectedDatabases.length === 0) return 3;

    // Steps 4-7 use guideSubStep for manual progression
    if (guideSubStep !== null) return guideSubStep;
    return 4; // Default to Review Users after databases selected
  };

  // Advance to next guide sub-step (for steps 4-7)
  const advanceGuideStep = () => {
    const current = getCurrentGuideStep();
    if (current < 7) {
      setGuideSubStep(current + 1);
    }
  };

  // Start Guide Me mode
  const startGuideMe = () => {
    // Reset selections when starting guide
    setSelectedCampaign(null);
    setSelectedPrivilegeLevel(null);
    setSelectedRiskLevel(null);
    setSelectedDatabases([]);
    setGuideSubStep(null);
    setGuideMode(true);
  };

  // Exit Guide Me mode
  const exitGuideMe = () => {
    setGuideMode(false);
    setGuideSubStep(null);
  };

  // Reset to page 1 when filters or sorting changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortConfig, viewFilter, selectedCampaign, selectedPrivilegeLevel, selectedRiskLevel]);

  // Handle drag events for floating action bar
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setFloatingBarPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Reset floating bar position when selection is cleared
  useEffect(() => {
    if (selectedUsers.length === 0) {
      setFloatingBarPosition({ x: null, y: null });
    }
  }, [selectedUsers]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  useEffect(() => {
    loadAllAttestations();
  }, []);

  const loadAllAttestations = async (options = {}) => {
    const { skipCompletionCheck = false, calledAfterSave = false } = options;
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setStatusRibbon(null);

      // First, get all campaigns in ATTESTATION status
      const campaignsResponse = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const campaignsData = await campaignsResponse.json();

      const attestationCampaigns = campaignsData.campaigns.filter(
        campaign => campaign.status === 'ATTESTATION'
      );

      if (attestationCampaigns.length === 0) {
        if (!calledAfterSave) {
          setError('No campaigns in ATTESTATION status found');
        }
        setLoading(false);
        return;
      }

      // Store campaign details (including attestation_deadline) for display
      const campaignDetailsMap = {};
      attestationCampaigns.forEach(campaign => {
        campaignDetailsMap[campaign.campaign_name] = {
          campaign_id: campaign.campaign_id,
          attestation_deadline: campaign.attestation_deadline,
          end_date: campaign.end_date,
          start_date: campaign.start_date
        };
      });
      setCampaignDetails(campaignDetailsMap);

      // Load users from all attestation campaigns
      const allUsers = [];
      for (const campaign of attestationCampaigns) {
        const params = new URLSearchParams();
        params.append('campaign_id', campaign.campaign_id);
        params.append('filter_in_scope', 'true');
        // DI-12: "My Attestations" is always personal — scope to the logged-in
        // attester server-side. Send BOTH the drift-proof id (enterprise_user_id ->
        // manager_id) AND the email; the backend matches a row if EITHER key fits
        // (OR), so a future id/email desync in either column cannot silently hide an
        // owned row. Guards are truthy so no literal "undefined" is ever sent; when
        // the user has neither key the client fallback (below) fails closed to [].
        // Admin full-roster oversight lives on the separate "Manage Attestations"
        // screen (ManageAttestations.js / attestation/records/), which is unaffected.
        if (user?.enterprise_user_id) {
          params.append('attester_id', user.enterprise_user_id);
        }
        if (user?.email) {
          params.append('attester_email', user.email);
        }
        params.append('_t', Date.now());

        const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/prepare/?${params.toString()}`));
        const data = await response.json();

        if (data.success && data.managers) {
          const campaignUsers = data.managers.flatMap(manager =>
            manager.assigned_users.map(user => ({
              ...user,
              campaign_id: campaign.campaign_id,
              campaign_name: campaign.campaign_name,
              manager_name: manager.manager_name,
              manager_email: manager.manager_email,
              manager_id: manager.manager_id
            }))
          );

          // DEBUG: Log group records
          const groupRecords = campaignUsers.filter(u => u.is_group === true);
          if (groupRecords.length > 0) {
            console.log('DEBUG: Found group records:', groupRecords.map(g => ({
              db_username: g.db_username,
              is_group: g.is_group,
              access_source: g.access_source,
              manager_email: g.manager_email
            })));
          }

          allUsers.push(...campaignUsers);
        }
      }

      // Filter users based on role
      let filteredData = allUsers;

      // DEBUG: Log user object and filtering
      console.log('DEBUG ManagerAttestations - User object:', {
        email: user?.email,
        enterprise_user_id: user?.enterprise_user_id,
        role: user?.role,
        can_view_all_data: user?.role?.can_view_all_data
      });
      console.log('DEBUG ManagerAttestations - Total allUsers:', allUsers.length);
      console.log('DEBUG ManagerAttestations - Sample manager_ids from users:',
        allUsers.slice(0, 5).map(u => ({ db_username: u.db_username, manager_id: u.manager_id, manager_email: u.manager_email }))
      );

      if (user && (user.enterprise_user_id || user.email)) {
        // DI-12: "My Attestations" is ALWAYS personal — scope every user
        // (admins included) to the rows where they are the attester. The old
        // `!user.role.can_view_all_data` escape hatch let admins see all rows
        // here; that admin full-roster view belongs on "Manage Attestations".
        // This is client-side defense-in-depth; the server already scoped via
        // attester_email, so this normally no-ops. Scope by enterprise_user_id
        // (manager_id) when present, else fall back to email (manager_email) so
        // an attester linked only by email is still scoped — never the full set.
        filteredData = allUsers.filter(u =>
          (user.enterprise_user_id && u.manager_id === user.enterprise_user_id) ||
          (user.email && u.manager_email && u.manager_email.toUpperCase() === user.email.toUpperCase())
        );
        console.log('DEBUG ManagerAttestations - After filtering (personal scope):', filteredData.length, 'users');
      } else {
        // DI-12 fail-closed: no identity key we can scope by -> show NOTHING,
        // never the full campaign roster. (Admin oversight lives on the separate
        // "Manage Attestations" screen.)
        filteredData = [];
        console.log('DEBUG ManagerAttestations - No identity key (no enterprise_user_id/email) -> empty personal list', {
          hasUser: !!user,
          hasEnterpriseUserId: !!user?.enterprise_user_id,
          hasEmail: !!user?.email
        });
      }

      // DEBUG: Log final filtered groups
      const finalGroups = filteredData.filter(u => u.is_group === true);
      if (finalGroups.length > 0) {
        console.log('DEBUG: Final filtered groups:', finalGroups.map(g => ({
          db_username: g.db_username,
          is_group: g.is_group,
          manager_id: g.manager_id
        })));
      } else {
        console.log('DEBUG: No groups in final filtered data. Total users:', filteredData.length);
      }

      setUsers(filteredData);
      setFilteredUsers(filteredData);
      setStats({
        total_users: filteredData.length,
        total_campaigns: attestationCampaigns.length,
        pending_decisions: filteredData.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending').length,
        completed_decisions: filteredData.filter(u => u.attestation_decision && u.attestation_decision !== 'Pending').length,
        completed_retained: filteredData.filter(u => u.attestation_decision === 'Retained').length,
        completed_modified: filteredData.filter(u => u.attestation_decision === 'Modify').length,
        completed_removed: filteredData.filter(u => u.attestation_decision === 'Removed' || u.attestation_decision === 'Rejected').length
      });

      // Extract unique values for filter dropdowns
      const attestationDecisions = [...new Set(filteredData.map(u => u.attestation_decision || 'Pending').filter(Boolean))].sort();

      // Fetch matrix-enabled tiers for each unique technology present in the loaded data.
      // Drives severity-ordered sidebar groups, table filter sort, and Modify dropdown options.
      const uniqueTechs = [...new Set(filteredData.map(u => (u.database_type || '').toLowerCase()).filter(Boolean))];
      const tierMap = {};
      await Promise.all(uniqueTechs.map(async (tech) => {
        try {
          const res = await fetch(API_CONFIG.getDatabasesEndpoint(`privilege-matrix/${tech}/active-tiers/`));
          const json = await res.json();
          if (json.success && Array.isArray(json.tiers)) {
            tierMap[tech] = json.tiers;
          }
        } catch (e) {
          console.warn(`Failed to load privilege matrix tiers for ${tech}:`, e);
        }
      }));
      setMatrixTiersByTech(tierMap);

      // Build a combined tier_value lookup across all techs in scope; if a tier appears
      // in multiple techs (which it always does — tier_name is canonical), use its tier_value.
      const combinedTierValue = {};
      Object.values(tierMap).forEach(tiers => {
        tiers.forEach(t => { combinedTierValue[t.tier_name] = t.tier_value ?? 0; });
      });

      setFilterOptions({
        campaigns: [...new Set(filteredData.map(u => u.campaign_name).filter(Boolean))].sort(),
        databases: [...new Set(filteredData.map(u => u.database_name).filter(Boolean))].sort(),
        db_usernames: [...new Set(filteredData.map(u => u.db_username).filter(Boolean))].sort(),
        employees: [...new Set(filteredData.map(u => u.user_display_name).filter(v => v && v !== 'Unknown User'))].sort(),
        applications: [...new Set(filteredData.map(u => u.application_name).filter(Boolean))].sort(),
        attesters: [...new Set(filteredData.map(u => u.manager_name || u.manager_email).filter(Boolean))].sort(),
        // Sort by matrix severity descending (ADMIN > WRITE > READ > NONE); falls back to
        // alphabetical for tiers not found in any tech's matrix (shouldn't happen in practice).
        privilege_levels: [...new Set(filteredData.map(u => u.privilege_level).filter(Boolean))]
          .sort((a, b) => {
            const va = combinedTierValue[a];
            const vb = combinedTierValue[b];
            if (va !== undefined && vb !== undefined) return vb - va;
            return a.localeCompare(b);
          }),
        risk_levels: ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'],
        attestation_decisions: attestationDecisions
      });

      // Fetch advanced statistics for the first campaign
      if (attestationCampaigns.length > 0) {
        fetchAdvancedStatistics(
          attestationCampaigns[0].campaign_id,
          attestationCampaigns[0].campaign_name
        );
      }

      // Check if all users are already attested (skip when called after saving - handleSaveDecisions handles it)
      if (!skipCompletionCheck) {
        const pendingCount = filteredData.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending').length;
        if (filteredData.length > 0 && pendingCount === 0) {
          // All users are attested - show notification modal
          setAllAttestedModalType('attested');
          setAllAttestedModalMessage('All users have been attested. To view or change your attestation decisions, select a campaign and then a privilege level from the sidebar and then click on "Attested Users" or "All Users" filter above.');
          setShowAllAttestedModal(true);
        }
      }

      setLoading(false);
    } catch (err) {
      setError('Error loading attestations: ' + err.message);
      setLoading(false);
    }
  };

  // Check for completed campaigns (not in ATTESTATION status) when user has no pending attestations
  const checkForCompletedCampaigns = async () => {
    try {
      const campaignsResponse = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const campaignsData = await campaignsResponse.json();

      // Check if there are any COMPLETED campaigns where this user was an attester
      const completedCampaigns = campaignsData.campaigns.filter(
        campaign => campaign.status === 'COMPLETED'
      );

      if (completedCampaigns.length > 0 && users.length === 0) {
        setAllAttestedModalType('closed');
        setAllAttestedModalMessage('The campaign is now closed and no more changes are allowed.');
        setShowAllAttestedModal(true);
      }
    } catch (err) {
      console.error('Error checking completed campaigns:', err);
    }
  };

  const fetchAdvancedStatistics = async (campaignId, campaignName) => {
    try {
      console.log('Fetching advanced statistics for:', { campaignId, campaignName, email: user.email });
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/statistics/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          campaign_name: campaignName,
          attester_email: user.email
        })
      });

      const data = await response.json();
      console.log('Advanced statistics response:', data);

      if (data.success) {
        setAdvancedStats(data.statistics);
        console.log('Advanced statistics set:', data.statistics);
      } else {
        console.error('Statistics API returned error:', data.error);
      }
    } catch (err) {
      console.error('Error fetching advanced statistics:', err);
    }
  };

  // Check if the attester is trying to attest their own account.
  // Uses the auth context 'user' (attester) from component scope, so must be
  // called BEFORE entering functions where the 'user' parameter shadows it.
  const isSelfAttestation = (accountRecord) => {
    const attesterEmail = user?.email?.toLowerCase();
    const accountEmail = accountRecord?.user_email?.toLowerCase();
    const attesterEmpId = user?.enterprise_user_id;
    const accountEmpId = accountRecord?.employee_id;
    // Match by email or employee ID
    if (attesterEmail && accountEmail && attesterEmail === accountEmail) return true;
    if (attesterEmpId && accountEmpId && attesterEmpId === accountEmpId) return true;
    return false;
  };

  // Self-attestation guard for the delegation pickers: the human behind any of
  // the report rows being delegated may not be offered as delegate attester —
  // the attest-time guard above would block them anyway, leaving a dead-end
  // assignment. The delegate endpoint enforces the same rule server-side.
  const ownsAnyOfRows = (entUser, rows) => rows.some(r =>
    (r?.user_email && entUser.user_email && r.user_email.toLowerCase() === entUser.user_email.toLowerCase()) ||
    (r?.employee_id && entUser.employee_id && r.employee_id === entUser.employee_id)
  );

  const handleDecisionChange = (userKey, decision, user) => {
    // Prevent self-attestation — attester cannot attest their own account
    if (isSelfAttestation(user)) {
      setSelfAttestUser(user);
      setShowSelfAttestModal(true);
      return;
    }

    // If "Modify" is selected, check for system account warning
    if (decision === 'Modify') {
      // Check if this is a system account (Oracle maintained, Cassandra default, etc.)
      if (user.is_system_account) {
        setPendingSystemAccountDecision({ userKey, decision, user });
        setShowSystemAccountWarningModal(true);
        return;
      }
      setModifyModalUser({ ...user, userKey });
      setShowModifyModal(true);
      return;
    }

    // If "Removed" is selected for a system account, show warning
    if (decision === 'Removed' && user.is_system_account) {
      setPendingSystemAccountDecision({ userKey, decision, user });
      setShowSystemAccountWarningModal(true);
      return;
    }

    setDecisions(prev => {
      const currentDecision = prev[userKey]?.decision;
      // If clicking the same decision again, unselect it
      if (currentDecision === decision) {
        const { [userKey]: _, ...rest} = prev;
        return rest;
      }
      // Otherwise, set the new decision
      return {
        ...prev,
        [userKey]: {
          ...prev[userKey],
          decision: decision
        }
      };
    });
  };

  const handleCommentChange = (userKey, comment) => {
    setDecisions(prev => ({
      ...prev,
      [userKey]: {
        ...prev[userKey],
        comment: comment
      }
    }));
  };

  // Group attestation helper functions
  const isGroupRecord = (user) => {
    const result = user.is_group === true;
    if (user.db_username && user.db_username.includes('Administrator')) {
      console.log('DEBUG isGroupRecord for', user.db_username, ':', {
        is_group: user.is_group,
        is_group_type: typeof user.is_group,
        result: result,
        access_source: user.access_source
      });
    }
    return result;
  };

  const isGroupMember = (user) => {
    return user.is_group_member === true;
  };

  const loadGroupMembers = async (campaignId, groupName, campaignName = '') => {
    setLoadingGroupMembers(true);
    try {
      const params = new URLSearchParams({
        campaign_id: campaignId,
        group_name: groupName
      });

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`attestation/group-members/?${params.toString()}`)
      );
      const data = await response.json();

      if (data.success) {
        setGroupMembers(data.members);
        setSelectedGroup({
          campaign_id: campaignId,
          campaign_name: campaignName,
          group_name: groupName,
          total: data.total
        });
        setShowGroupMembersModal(true);
      } else {
        setError(`Error loading group members: ${data.error}`);
      }
    } catch (err) {
      console.error('Error loading group members:', err);
      setError('Failed to load group members');
    } finally {
      setLoadingGroupMembers(false);
    }
  };

  const updateGroupProgress = async (campaignId, groupName) => {
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('attestation/update-group-progress/'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            group_name: groupName
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        // Refresh the attestations list to show updated progress
        await loadAllAttestations();
        return data;
      } else {
        console.error('Error updating group progress:', data.error);
      }
    } catch (err) {
      console.error('Error updating group progress:', err);
    }
  };

  const handleGroupMemberAttestation = async (member, decision, comments) => {
    try {
      // Set loading state for this member
      setAttestingMemberId(member.report_id);

      // Build attestation payload
      const attestations = [{
        report_id: member.report_id,
        campaign_id: member.campaign_id || selectedGroup.campaign_id,
        database_name: member.database_name,
        db_username: member.db_username,
        attestation_decision: decision,
        attestation_comments: comments || `Attested via group: ${selectedGroup.group_name}`,
        target_privilege_level: decision === 'Modify' ? member.target_privilege_level : null
      }];

      // Submit attestation
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/update/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attestations })
      });

      const data = await response.json();

      if (data.success) {
        // Update group progress
        const progressData = await updateGroupProgress(
          selectedGroup.campaign_id,
          selectedGroup.group_name
        );

        // Reload group members to show updated status
        await loadGroupMembers(selectedGroup.campaign_id, selectedGroup.group_name, selectedGroup.campaign_name);

        // Show notification if group was auto-attested
        if (progressData && progressData.can_attest) {
          const ruleMessage = progressData.group_attestation_rule === 'ALL_REMOVED'
            ? `All members removed - group automatically removed`
            : `${progressData.members_retained} of ${progressData.members_in_scope} members retained - group automatically retained`;

          setStatusRibbon({
            tone: 'success',
            title: 'Group attestation complete',
            details: [ruleMessage],
            autoDismissMs: 10000
          });

          // Reload main attestations list to show group as attested
          await loadAllAttestations();
        }
      } else {
        setError(`Error attesting member: ${data.error}`);
      }
    } catch (err) {
      console.error('Error in handleGroupMemberAttestation:', err);
      setError('Failed to attest group member');
    } finally {
      // Clear loading state
      setAttestingMemberId(null);
    }
  };

  // Handle confirming the Modify decision from the modal
  const handleConfirmModify = () => {
    if (!targetPrivilegeLevel) {
      alert('Please select a target privilege level');
      return;
    }
    if (!modifyComment.trim()) {
      alert('Comments are mandatory for Modify decisions. Please provide a comment explaining the modification.');
      return;
    }

    const { userKey, ...userData } = modifyModalUser;

    // Set the decision with the target privilege level and comment
    setDecisions(prev => ({
      ...prev,
      [userKey]: {
        decision: 'Modify',
        comment: modifyComment,
        targetPrivilegeLevel: targetPrivilegeLevel
      }
    }));

    // Close modal and reset state
    setShowModifyModal(false);
    setModifyModalUser(null);
    setTargetPrivilegeLevel('');
    setModifyComment('');
  };

  // Handle closing the Modify modal
  const handleCloseModifyModal = () => {
    setShowModifyModal(false);
    setModifyModalUser(null);
    setTargetPrivilegeLevel('');
    setModifyComment('');
  };

  // Handle system account warning confirmation
  const handleConfirmSystemAccountWarning = () => {
    if (!pendingSystemAccountDecision) return;

    const { userKey, decision, user } = pendingSystemAccountDecision;

    if (decision === 'Modify') {
      // Proceed with the Modify modal
      setModifyModalUser({ ...user, userKey });
      setShowModifyModal(true);
    } else if (decision === 'Removed') {
      // Proceed with Remove decision
      setDecisions(prev => ({
        ...prev,
        [userKey]: {
          ...prev[userKey],
          decision: 'Removed'
        }
      }));
    }

    // Close system account warning modal
    setShowSystemAccountWarningModal(false);
    setPendingSystemAccountDecision(null);
  };

  // Handle system account warning cancellation
  const handleCancelSystemAccountWarning = () => {
    setShowSystemAccountWarningModal(false);
    setPendingSystemAccountDecision(null);
  };

  // Get available lower privilege levels for the user, restricted to the matrix-enabled
  // tier set for the user's technology. Returns names in ascending severity (lowest first).
  // Falls back to a static 7-tier list if the matrix has not loaded yet, so the dropdown
  // is never empty mid-fetch.
  const getAvailablePrivilegeLevels = (currentLevel, dbType) => {
    const techKey = (dbType || '').toLowerCase();
    const matrixTiers = matrixTiersByTech[techKey];
    const fallback = [
      { tier_name: 'NONE', tier_value: 0 },
      { tier_name: 'READ', tier_value: 1 },
      { tier_name: 'READ_ELEVATED', tier_value: 2 },
      { tier_name: 'WRITE', tier_value: 3 },
      { tier_name: 'WRITE_ELEVATED', tier_value: 4 },
      { tier_name: 'ADMIN', tier_value: 5 },
      { tier_name: 'SUPER_ADMIN', tier_value: 6 }
    ];
    const tiers = (Array.isArray(matrixTiers) && matrixTiers.length > 0) ? matrixTiers : fallback;
    const sorted = [...tiers].sort((a, b) => (a.tier_value ?? 0) - (b.tier_value ?? 0));
    const currentTier = sorted.find(t => t.tier_name === currentLevel);
    if (!currentTier) return [];
    return sorted.filter(t => (t.tier_value ?? 0) < (currentTier.tier_value ?? 0)).map(t => t.tier_name);
  };

  const handleSaveDecisions = async () => {
    const decisionsToSave = Object.entries(decisions).filter(([_, data]) => data.decision);

    if (decisionsToSave.length === 0) {
      setError('No decisions to save. Please make at least one attestation decision.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const attestations = decisionsToSave
        .map(([userKey, data]) => {
          const user = users.find(u =>
            `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey
          );
          // Safety net: exclude self-attestation entries
          if (user && isSelfAttestation(user)) return null;

          return {
            campaign_id: user.campaign_id,
            database_name: user.database_name,
            db_username: user.db_username,
            attestation_decision: data.decision,
            attestation_comments: data.comment || '',
            attester_email: user.manager_email,
            target_privilege_level: data.targetPrivilegeLevel || null
          };
        })
        .filter(Boolean);

      if (attestations.length === 0) {
        setError('No valid decisions to save. Self-attestation is not allowed.');
        setLoading(false);
        return;
      }

      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/update/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attestations: attestations
        })
      });

      const result = await response.json();

      if (result.success) {
        const savedCount = result.updated_count || attestations.length;
        const ribbonDetails = [];
        if (result.campaign_completed && result.completed_campaign_ids) {
          ribbonDetails.push(
            `🎉 Campaign(s) completed: ${result.completed_campaign_ids.join(', ')}. The campaign is now read-only.`
          );
        }

        setDecisions({});
        setSelectedUsers([]);

        // Calculate remaining pending BEFORE reload (reload may lose completed campaigns)
        const savedUserKeys = new Set(attestations.map(a => `${a.campaign_id}_${a.database_name}_${a.db_username}`));
        const remainingPending = users.filter(u => {
          const key = `${u.campaign_id}_${u.database_name}_${u.db_username}`;
          // If this user was just saved, it's no longer pending
          if (savedUserKeys.has(key)) return false;
          // Otherwise check if it's still pending
          return !u.attestation_decision || u.attestation_decision === 'Pending';
        }).length;

        // Reload all attestations to get updated data (skip completion check - we handle it below)
        await loadAllAttestations({ skipCompletionCheck: true, calledAfterSave: true });

        // Show completion modal if attester has finished all their work
        // This handles the case where campaign_completed causes the campaign to
        // move to COMPLETED status, making it invisible to loadAllAttestations
        // (which only loads ATTESTATION campaigns), so the post-reload check misses it
        if (remainingPending === 0 && users.length > 0) {
          if (result.campaign_completed) {
            setAllAttestedModalType('closed');
            setAllAttestedModalMessage('All attestations have been completed and the campaign is now closed. No further changes are allowed.');
          } else {
            setAllAttestedModalType('attested');
            setAllAttestedModalMessage('You have completed all your assigned attestations! To view or change your decisions, select a campaign and privilege level from the sidebar.');
          }
          setShowAllAttestedModal(true);
        }

        setStatusRibbon({
          tone: 'success',
          title: `Successfully saved ${savedCount} attestation decision(s)`,
          details: ribbonDetails,
          autoDismissMs: ribbonDetails.length > 0 ? null : 10000
        });
      } else {
        setError('Failed to save decisions: ' + (result.error || 'Unknown error'));
        setLoading(false);
      }
    } catch (err) {
      setError('Error saving decisions: ' + err.message);
      setLoading(false);
    }
  };

  // Handle clearing attestation decisions for selected users
  const handleClearAttestations = async () => {
    // Get the attested users from selection
    const attestedUserKeys = selectedUsers.filter(userKey => {
      const user = users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey);
      return user && user.attestation_decision && user.attestation_decision !== 'Pending';
    });

    if (attestedUserKeys.length === 0) {
      setShowClearAttestationModal(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const attestations = attestedUserKeys.map(userKey => {
        const user = users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey);
        return {
          campaign_id: user.campaign_id,
          database_name: user.database_name,
          db_username: user.db_username,
          attestation_decision: 'Pending',
          attestation_comments: '',
          attester_email: user.manager_email,
          target_privilege_level: null
        };
      });

      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/update/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attestations: attestations
        })
      });

      const result = await response.json();

      if (result.success) {
        setStatusRibbon({
          tone: 'success',
          title: `Successfully cleared ${attestations.length} attestation decision(s)`,
          details: ['You can now make new decisions.'],
          autoDismissMs: 10000
        });
        setShowClearAttestationModal(false);
        setSelectedUsers([]);
        // Reload all attestations to get updated data
        await loadAllAttestations();
      } else {
        setError('Failed to clear attestations: ' + (result.error || 'Unknown error'));
        setLoading(false);
      }
    } catch (err) {
      setError('Error clearing attestations: ' + err.message);
      setLoading(false);
    }
  };

  // Filtering logic
  useEffect(() => {
    let result = [...users];

    // Apply hierarchical sidebar filters (Campaign → Privilege Level → Risk Level → Database)
    if (selectedCampaign) {
      result = result.filter(u => u.campaign_name === selectedCampaign);
    }
    if (selectedPrivilegeLevel) {
      result = result.filter(u => u.privilege_level === selectedPrivilegeLevel);
    }
    if (selectedRiskLevel) {
      result = result.filter(u => u.risk_level === selectedRiskLevel);
    }
    if (selectedDatabases.length > 0) {
      result = result.filter(u => selectedDatabases.includes(u.database_name));
    }

    // Apply remaining table filters (database, db_username, employee, application, attester, decision)
    if (filters.database.length > 0) {
      result = result.filter(u => filters.database.includes(u.database_name));
    }
    if (filters.db_username.length > 0) {
      result = result.filter(u => filters.db_username.includes(u.db_username));
    }
    if (filters.employee.length > 0) {
      result = result.filter(u => filters.employee.includes(u.user_display_name));
    }
    if (filters.application.length > 0) {
      result = result.filter(u => filters.application.includes(u.application_name));
    }
    if (filters.attester.length > 0) {
      result = result.filter(u =>
        filters.attester.includes(u.manager_name) || filters.attester.includes(u.manager_email)
      );
    }
    if (filters.attestation_decision.length > 0) {
      result = result.filter(u =>
        filters.attestation_decision.includes(u.attestation_decision || 'Pending')
      );
    }

    // Calculate stats from filtered data (WITHOUT viewFilter applied)
    const newStats = {
      total_users: result.length,
      total_campaigns: [...new Set(result.map(u => u.campaign_id))].length,
      pending_decisions: result.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending').length,
      completed_decisions: result.filter(u => u.attestation_decision && u.attestation_decision !== 'Pending').length,
      completed_retained: result.filter(u => u.attestation_decision === 'Retained').length,
      completed_modified: result.filter(u => u.attestation_decision === 'Modify').length,
      completed_removed: result.filter(u => u.attestation_decision === 'Removed' || u.attestation_decision === 'Rejected').length
    };

    setStats(newStats);

    // Now apply view filter (pending, attested, or all) for display
    if (viewFilter === 'pending') {
      result = result.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending');
    } else if (viewFilter === 'attested') {
      result = result.filter(u => u.attestation_decision && u.attestation_decision !== 'Pending');
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        // Special handling for risk_level sorting
        if (sortConfig.key === 'risk_level') {
          const RISK_LEVEL_ORDER = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
          aVal = RISK_LEVEL_ORDER[a.risk_level] || 0;
          bVal = RISK_LEVEL_ORDER[b.risk_level] || 0;
        } else {
          if (typeof aVal === 'string') aVal = aVal.toLowerCase();
          if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredUsers(result);
  }, [users, filters, sortConfig, viewFilter, selectedCampaign, selectedPrivilegeLevel, selectedRiskLevel, selectedDatabases]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => {
      const currentValues = prev[column];
      if (currentValues.includes(value)) {
        // Remove value
        return {
          ...prev,
          [column]: currentValues.filter(v => v !== value)
        };
      } else {
        // Add value
        return {
          ...prev,
          [column]: [...currentValues, value]
        };
      }
    });
  };

  const handleSelectAll = (column, options) => {
    setFilters(prev => ({
      ...prev,
      [column]: prev[column].length === options.length ? [] : [...options]
    }));
  };

  const clearFilters = () => {
    setFilters({
      campaign: [],
      database: [],
      db_username: [],
      employee: [],
      application: [],
      attester: [],
      privilege_level: [],
      risk_level: [],
      attestation_decision: []
    });
    setSortConfig({ key: null, direction: 'asc' });
  };

  const handleSelectUser = (userKey) => {
    setSelectedUsers(prev => {
      if (prev.includes(userKey)) {
        return prev.filter(key => key !== userKey);
      } else {
        return [...prev, userKey];
      }
    });
  };

  const handleSelectAllUsers = () => {
    const currentPageKeys = paginatedUsers.map(u => `${u.campaign_id}_${u.database_name}_${u.db_username}`);
    const allCurrentPageSelected = currentPageKeys.every(key => selectedUsers.includes(key));

    if (allCurrentPageSelected) {
      // Deselect all users on current page
      setSelectedUsers(selectedUsers.filter(key => !currentPageKeys.includes(key)));
    } else {
      // Select all users on current page
      const newSelection = [...new Set([...selectedUsers, ...currentPageKeys])];
      setSelectedUsers(newSelection);
    }
  };

  const handleBulkDecision = (decision) => {
    // For Modify decision, open the bulk modify modal instead of applying directly
    if (decision === 'Modify') {
      // Get pending users from selection, excluding self-attestation accounts
      const pendingUsers = selectedUsers
        .map(userKey => {
          const user = users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey);
          return user && (!user.attestation_decision || user.attestation_decision === 'Pending') && !isSelfAttestation(user)
            ? { ...user, userKey }
            : null;
        })
        .filter(Boolean);

      if (pendingUsers.length > 0) {
        // Bulk Modify requires all selected accounts to share one privilege level so the
        // same target level is meaningful for every row.
        const distinctLevels = [...new Set(pendingUsers.map(u => u.privilege_level || 'Unknown'))];
        if (distinctLevels.length > 1) {
          setMixedPrivilegeLevels(distinctLevels.sort());
          setShowMixedPrivilegeModal(true);
          return;
        }

        setBulkModifyUsers(pendingUsers);
        setBulkTargetPrivilegeLevel('');
        setBulkModifyComment('');
        setShowBulkModifyModal(true);
      }
      return;
    }

    const newDecisions = { ...decisions };
    let attestedCount = 0;
    let updatedCount = 0;
    let selfAttestCount = 0;

    selectedUsers.forEach(userKey => {
      // Find the user to check if already attested
      const user = users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey);

      // Skip self-attestation accounts
      if (user && isSelfAttestation(user)) {
        selfAttestCount++;
        return;
      }

      // Only apply decision if user is not already attested (pending or no decision)
      if (user && (!user.attestation_decision || user.attestation_decision === 'Pending')) {
        newDecisions[userKey] = {
          ...newDecisions[userKey],
          decision: decision
        };
        updatedCount++;
      } else {
        attestedCount++;
      }
    });

    setDecisions(newDecisions);
    // Don't clear selection immediately - let user see Save button and click it
    // setSelectedUsers([]);

    // Build tiered ribbon: success only if nothing was skipped, warning otherwise.
    const totalSkipped = attestedCount + selfAttestCount;
    const totalSelected = updatedCount + totalSkipped;
    if (totalSelected === 0) return;

    const details = [];
    if (attestedCount > 0) {
      details.push(`Skipped ${attestedCount} already-attested user(s).`);
    }
    if (selfAttestCount > 0) {
      details.push(`Skipped ${selfAttestCount} own account(s) — self-attestation is not allowed.`);
    }
    if (updatedCount > 0) {
      details.push('Click Save to submit.');
    }

    let tone, title;
    if (updatedCount === 0) {
      // Nothing applied — all rows were skipped
      tone = 'warning';
      title = `No ${decision} decision applied — all ${totalSkipped} selected row(s) were skipped`;
    } else if (totalSkipped > 0) {
      // Partial — some applied, some skipped
      tone = 'warning';
      title = `Applied ${decision} to ${updatedCount} of ${totalSelected} selected user(s) — ${totalSkipped} skipped`;
    } else {
      // Clean success
      tone = 'success';
      title = `Applied ${decision} decision to ${updatedCount} pending user(s)`;
    }

    setStatusRibbon({
      tone,
      title,
      details,
      autoDismissMs: tone === 'success' ? 10000 : null
    });
  };

  const loadEnterpriseUsers = async () => {
    try {
      const response = await fetch(API_CONFIG.getEnterpriseUsersEndpoint('?page_size=1000'));
      const data = await response.json();
      console.log('Enterprise users API response:', data);
      console.log('Results count:', data.results ? data.results.length : 0);
      // Handle Django REST Framework paginated response
      const users = data.results || data.users || [];
      const mappedUsers = users.map(u => ({
        user_id: u.user_id,
        user_display_name: u.display_name,
        user_email: u.email,
        employee_id: u.employee_id,
        job_title: u.job_title || '',
        department: u.department || ''
      }));
      console.log('Mapped users count:', mappedUsers.length);
      setEnterpriseUsers(mappedUsers);
    } catch (err) {
      console.error('Error loading enterprise users:', err);
    }
  };

  // Load enterprise users when delegate modal opens
  useEffect(() => {
    if ((showDelegateModal || showUserDelegateModal) && enterpriseUsers.length === 0) {
      console.log('Delegate modal opened, loading enterprise users...');
      loadEnterpriseUsers();
    }
  }, [showDelegateModal, showUserDelegateModal]);

  const handleDelegate = async () => {
    if (!delegateAttester.id || !delegateAttester.name || !delegateAttester.email) {
      alert('Please select a delegate attester');
      return;
    }

    try {
      setLoading(true);

      // Bulk delegation: Get ALL pending users for this manager (not just selected)
      const allPendingUsers = users.filter(u =>
        !u.attestation_decision || u.attestation_decision === 'Pending'
      );

      if (allPendingUsers.length === 0) {
        setError('No pending users to delegate. All users have already been attested.');
        setLoading(false);
        return;
      }

      // Confirm bulk delegation
      const confirmMessage = `This will reassign ALL ${allPendingUsers.length} pending attestation(s) to ${delegateAttester.name}. Are you sure?`;
      if (!window.confirm(confirmMessage)) {
        setLoading(false);
        return;
      }

      // Update the attester for ALL pending users
      const updatePromises = allPendingUsers.map(async (dbUser) => {
        const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/delegate/'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_id: dbUser.campaign_id,
            database_name: dbUser.database_name,
            db_username: dbUser.db_username,
            new_attester_id: delegateAttester.id,
            new_attester_name: delegateAttester.name,
            new_attester_email: delegateAttester.email,
            delegated_by: user?.email || 'unknown'
          })
        });

        return response.json();
      });

      await Promise.all(updatePromises);

      setStatusRibbon({
        tone: 'success',
        title: `Successfully delegated all ${allPendingUsers.length} pending attestation(s)`,
        details: [`Delegated to ${delegateAttester.name}.`],
        autoDismissMs: 10000
      });
      setShowDelegateModal(false);
      setDelegateAttester({ id: '', name: '', email: '' });
      setDelegateSearchTerm('');
      setSelectedUsers([]);
      loadAllAttestations();
    } catch (err) {
      setError(`Error delegating attestations: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handler for user-level delegation (single user or selected users)
  const handleUserDelegate = async () => {
    if (!delegateAttester.id || !delegateAttester.name || !delegateAttester.email) {
      alert('Please select a delegate attester');
      return;
    }

    try {
      setLoading(true);

      // Get the users to delegate based on usersToDelegateKeys
      const usersToDelegate = usersToDelegateKeys
        .map(userKey => users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey))
        .filter(u => u && (!u.attestation_decision || u.attestation_decision === 'Pending'));

      if (usersToDelegate.length === 0) {
        setError('No pending users to delegate. Selected user(s) have already been attested.');
        setLoading(false);
        return;
      }

      // Update the attester for the selected users
      const updatePromises = usersToDelegate.map(async (dbUser) => {
        const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/delegate/'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_id: dbUser.campaign_id,
            database_name: dbUser.database_name,
            db_username: dbUser.db_username,
            new_attester_id: delegateAttester.id,
            new_attester_name: delegateAttester.name,
            new_attester_email: delegateAttester.email,
            delegated_by: user?.email || 'unknown'
          })
        });

        return response.json();
      });

      await Promise.all(updatePromises);

      setStatusRibbon({
        tone: 'success',
        title: `Successfully delegated ${usersToDelegate.length} user(s)`,
        details: [`Delegated to ${delegateAttester.name}.`],
        autoDismissMs: 10000
      });
      setShowUserDelegateModal(false);
      setUsersToDelegateKeys([]);
      setDelegateAttester({ id: '', name: '', email: '' });
      setDelegateSearchTerm('');
      setSelectedUsers([]);
      loadAllAttestations();
    } catch (err) {
      setError(`Error delegating attestations: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Open user delegation modal for a single user or selected users
  const openUserDelegateModal = (userKey = null) => {
    if (userKey) {
      // Single user delegation
      setUsersToDelegateKeys([userKey]);
    } else if (selectedUsers.length > 0) {
      // Selected users delegation
      setUsersToDelegateKeys([...selectedUsers]);
    }
    setShowUserDelegateModal(true);
  };

  // Handler to fetch and display user privilege details
  const handleShowPrivilegeDetails = async (user) => {
    setPrivilegeDetailLoading(true);
    setShowPrivilegeDetailModal(true);
    setPrivilegeDetailData({
      db_username: user.db_username,
      database_name: user.database_name,
      database_type: user.database_type,
      privilege_level: user.privilege_level,
      user_info_collected_date: user.user_info_collected_date
    });

    try {
      // Determine which endpoint to call based on database type
      let endpoint;
      const params = {
        database_name: user.database_name
      };

      if (user.user_info_collected_date) {
        params.collection_timestamp = user.user_info_collected_date;
      }

      console.log('=== Privilege Detail Request ===');
      console.log('Username:', user.db_username);
      console.log('Database:', user.database_name);
      console.log('Database Type:', user.database_type);
      console.log('Collection Timestamp:', user.user_info_collected_date);

      // Select appropriate endpoint based on database type
      if (user.database_type === 'MSSQL') {
        endpoint = `mssql-access/user/${encodeURIComponent(user.db_username)}/`;
      } else if (user.database_type === 'ORACLE') {
        endpoint = `oracle-access/user/${user.db_username}/`;
      } else if (user.database_type === 'MYSQL') {
        endpoint = `mysql-access/user/${user.db_username}/`;
      } else if (user.database_type === 'CASSANDRA') {
        endpoint = `cassandra-access/user/${encodeURIComponent(user.db_username)}/`;
      } else if (user.database_type === 'POSTGRESQL') {
        endpoint = `postgresql-access/user/${encodeURIComponent(user.db_username)}/`;
      } else {
        throw new Error(`Unsupported database type: ${user.database_type}`);
      }

      console.log('Request endpoint:', endpoint);
      console.log('Request params:', params);

      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(endpoint), {
        params: params
      });

      console.log('=== API Response ===');
      console.log('Full response:', response.data);

      if (response.data && response.data.user) {
        // Handle case where user is an array (user exists in multiple databases)
        const userData = Array.isArray(response.data.user) ? response.data.user[0] : response.data.user;
        console.log('=== User Data Fields ===');
        console.log('Database Type:', user.database_type);
        console.log('User Data (isArray:', Array.isArray(response.data.user), '):', userData);

        let allPrivileges = [];
        let roleNames = [];
        let stats = {};

        // Handle different database types
        if (user.database_type === 'MSSQL') {
          // Parse MSSQL format
          const serverPerms = userData.server_permissions || [];
          const dbPerms = userData.database_permissions || [];
          const serverRoles = userData.server_role_memberships || [];
          const dbRoles = userData.database_role_memberships || [];

          console.log('Server permissions count:', serverPerms.length);
          console.log('Database permissions count:', dbPerms.length);
          console.log('Server roles count:', serverRoles.length);
          console.log('Database roles count:', dbRoles.length);

          // Add server permissions
          serverPerms.forEach(perm => {
            allPrivileges.push({
              name: perm.permission_name,
              privilege: perm.permission_name,
              source: `Server (${perm.state_desc})`,
              scope: perm.class_desc || 'SERVER'
            });
          });

          // Add database permissions
          dbPerms.forEach(perm => {
            const objectInfo = perm.object_name ? ` on ${perm.schema_name || ''}.${perm.object_name}` : '';
            allPrivileges.push({
              name: perm.permission_name + objectInfo,
              privilege: perm.permission_name,
              source: `Database (${perm.state_desc})`,
              scope: perm.class_desc || 'DATABASE',
              table: perm.object_name ? `${perm.schema_name || ''}.${perm.object_name}` : undefined
            });
          });

          // Extract role names
          roleNames = [
            ...serverRoles.map(r => `${r.role_name} (Server)`),
            ...dbRoles.map(r => `${r.role_name} (Database)`)
          ];

          stats = userData.stats || {};
        } else if (user.database_type === 'CASSANDRA') {
          // Parse Cassandra format
          const systemPrivileges = userData.system_privileges || [];
          const roles = userData.roles || [];

          console.log('Cassandra systemPrivileges count:', systemPrivileges.length);
          console.log('Cassandra roles count:', roles.length);

          // Add system privileges (Cassandra format has resource and permissions array)
          systemPrivileges.forEach(priv => {
            const permissions = priv.permissions || [];
            permissions.forEach(perm => {
              allPrivileges.push({
                name: `${perm} on ${priv.resource}`,
                privilege: perm,
                table: priv.resource,
                source: priv.grant_type === 'DIRECT' ? 'Direct' : `Via Role: ${priv.source || 'Unknown'}`,
                grant_type: priv.grant_type
              });
            });
          });

          // Extract role names (Cassandra format has role and admin_option)
          roleNames = roles.map(role => role.role || role.role_name || role);

          stats = userData.stats || {};
        } else if (user.database_type === 'POSTGRESQL') {
          // Parse PostgreSQL format
          const memberships = typeof userData.memberships === 'string'
            ? JSON.parse(userData.memberships)
            : (userData.memberships || []);
          const dbPrivileges = typeof userData.database_privileges === 'string'
            ? JSON.parse(userData.database_privileges)
            : (userData.database_privileges || []);
          const schemaPrivileges = typeof userData.schema_privileges === 'string'
            ? JSON.parse(userData.schema_privileges)
            : (userData.schema_privileges || []);
          const tablePrivileges = typeof userData.table_privileges === 'string'
            ? JSON.parse(userData.table_privileges)
            : (userData.table_privileges || []);
          const functionPrivileges = typeof userData.function_privileges === 'string'
            ? JSON.parse(userData.function_privileges)
            : (userData.function_privileges || []);

          console.log('PostgreSQL memberships count:', memberships.length);
          console.log('PostgreSQL dbPrivileges count:', dbPrivileges.length);
          console.log('PostgreSQL schemaPrivileges count:', schemaPrivileges.length);
          console.log('PostgreSQL tablePrivileges count:', tablePrivileges.length);
          console.log('PostgreSQL functionPrivileges count:', functionPrivileges.length);

          // Add special PostgreSQL privileges
          if (userData.is_superuser) {
            allPrivileges.push({
              name: 'SUPERUSER',
              privilege: 'SUPERUSER',
              source: 'Role Attribute',
              scope: 'GLOBAL'
            });
          }
          if (userData.can_create_db) {
            allPrivileges.push({
              name: 'CREATEDB',
              privilege: 'CREATEDB',
              source: 'Role Attribute',
              scope: 'GLOBAL'
            });
          }
          if (userData.can_create_role) {
            allPrivileges.push({
              name: 'CREATEROLE',
              privilege: 'CREATEROLE',
              source: 'Role Attribute',
              scope: 'GLOBAL'
            });
          }
          if (userData.is_replication) {
            allPrivileges.push({
              name: 'REPLICATION',
              privilege: 'REPLICATION',
              source: 'Role Attribute',
              scope: 'GLOBAL'
            });
          }
          if (userData.bypass_rls) {
            allPrivileges.push({
              name: 'BYPASSRLS',
              privilege: 'BYPASSRLS',
              source: 'Role Attribute',
              scope: 'GLOBAL'
            });
          }

          // Add database privileges (PostgreSQL format uses boolean flags)
          dbPrivileges.forEach(priv => {
            const dbName = priv.database_name || userData.database_name;
            if (priv.can_connect) {
              allPrivileges.push({ name: `CONNECT on DATABASE ${dbName}`, privilege: 'CONNECT', source: 'Direct', scope: 'DATABASE' });
            }
            if (priv.can_create) {
              allPrivileges.push({ name: `CREATE on DATABASE ${dbName}`, privilege: 'CREATE', source: 'Direct', scope: 'DATABASE' });
            }
            if (priv.can_temp) {
              allPrivileges.push({ name: `TEMPORARY on DATABASE ${dbName}`, privilege: 'TEMPORARY', source: 'Direct', scope: 'DATABASE' });
            }
          });

          // Add schema privileges (PostgreSQL format uses boolean flags)
          schemaPrivileges.forEach(priv => {
            if (priv.can_usage) {
              allPrivileges.push({ name: `USAGE on SCHEMA ${priv.schema_name}`, privilege: 'USAGE', table: priv.schema_name, source: 'Direct', scope: 'SCHEMA' });
            }
            if (priv.can_create) {
              allPrivileges.push({ name: `CREATE on SCHEMA ${priv.schema_name}`, privilege: 'CREATE', table: priv.schema_name, source: 'Direct', scope: 'SCHEMA' });
            }
          });

          // Add table privileges (PostgreSQL format uses boolean flags)
          tablePrivileges.forEach(priv => {
            const tableName = `${priv.schema_name}.${priv.table_name}`;
            if (priv.can_select) {
              allPrivileges.push({ name: `SELECT on ${tableName}`, privilege: 'SELECT', table: tableName, source: 'Direct', scope: 'TABLE' });
            }
            if (priv.can_insert) {
              allPrivileges.push({ name: `INSERT on ${tableName}`, privilege: 'INSERT', table: tableName, source: 'Direct', scope: 'TABLE' });
            }
            if (priv.can_update) {
              allPrivileges.push({ name: `UPDATE on ${tableName}`, privilege: 'UPDATE', table: tableName, source: 'Direct', scope: 'TABLE' });
            }
            if (priv.can_delete) {
              allPrivileges.push({ name: `DELETE on ${tableName}`, privilege: 'DELETE', table: tableName, source: 'Direct', scope: 'TABLE' });
            }
            if (priv.can_truncate) {
              allPrivileges.push({ name: `TRUNCATE on ${tableName}`, privilege: 'TRUNCATE', table: tableName, source: 'Direct', scope: 'TABLE' });
            }
            if (priv.can_references) {
              allPrivileges.push({ name: `REFERENCES on ${tableName}`, privilege: 'REFERENCES', table: tableName, source: 'Direct', scope: 'TABLE' });
            }
            if (priv.can_trigger) {
              allPrivileges.push({ name: `TRIGGER on ${tableName}`, privilege: 'TRIGGER', table: tableName, source: 'Direct', scope: 'TABLE' });
            }
          });

          // Add function privileges (PostgreSQL format uses boolean flags)
          functionPrivileges.forEach(priv => {
            const funcName = `${priv.schema_name}.${priv.function_name}`;
            if (priv.can_execute) {
              allPrivileges.push({ name: `EXECUTE on FUNCTION ${funcName}`, privilege: 'EXECUTE', table: funcName, source: 'Direct', scope: 'FUNCTION' });
            }
          });

          // Extract role names from memberships (PostgreSQL format has granted_role)
          roleNames = memberships.map(m => {
            if (typeof m === 'string') return m;
            return m.granted_role || m.role_name || m.rolname || 'Unknown Role';
          });

          stats = typeof userData.stats === 'string' ? JSON.parse(userData.stats) : (userData.stats || {});
        } else {
          // Parse Oracle/MySQL format
          const systemPrivileges = typeof userData.system_privileges === 'string'
            ? JSON.parse(userData.system_privileges)
            : (userData.system_privileges || []);
          const objectPrivileges = typeof userData.object_privileges === 'string'
            ? JSON.parse(userData.object_privileges)
            : (userData.object_privileges || []);
          const roles = typeof userData.roles === 'string'
            ? JSON.parse(userData.roles)
            : (userData.roles || []);

          console.log('systemPrivileges count:', systemPrivileges.length);
          console.log('objectPrivileges count:', objectPrivileges.length);
          console.log('roles count:', roles.length);

          // Add system privileges
          systemPrivileges.forEach(priv => {
            allPrivileges.push({
              name: priv.privilege,
              privilege: priv.privilege,
              source: priv.grant_type === 'DIRECT' ? 'Direct System' : `Via Role: ${priv.via_role || 'Unknown'}`,
              admin_option: priv.admin_option
            });
          });

          // Add object privileges
          objectPrivileges.forEach(priv => {
            allPrivileges.push({
              name: `${priv.privilege} on ${priv.owner}.${priv.object_name}`,
              privilege: priv.privilege,
              table: `${priv.owner}.${priv.object_name}`,
              source: priv.grant_type === 'DIRECT' ? 'Direct Object' : `Via Role: ${priv.via_role || 'Unknown'}`,
              grantable: priv.grantable
            });
          });

          // Extract role names
          roleNames = roles.map(role => role.granted_role || role.role_name);

          stats = typeof userData.stats === 'string' ? JSON.parse(userData.stats) : (userData.stats || {});
        }

        console.log('=== Final Parsed Data ===');
        console.log('allPrivileges count:', allPrivileges.length);
        console.log('roles count:', roleNames.length);
        console.log('stats:', stats);

        setPrivilegeDetailData({
          db_username: user.db_username,
          database_name: user.database_name,
          database_type: user.database_type,
          privilege_level: user.privilege_level,
          privileges: allPrivileges,
          roles: roleNames,
          stats: stats,
          user_details: userData.user_details,
          user_info_collected_date: user.user_info_collected_date
        });
      } else {
        // If no detailed data found, just show basic info
        setPrivilegeDetailData({
          db_username: user.db_username,
          database_name: user.database_name,
          database_type: user.database_type,
          privilege_level: user.privilege_level,
          privileges: [],
          roles: []
        });
      }
    } catch (error) {
      console.error('Error fetching privilege details:', error);
      setPrivilegeDetailData({
        db_username: user.db_username,
        database_name: user.database_name,
        database_type: user.database_type,
        privilege_level: user.privilege_level,
        error: 'Failed to load privilege details'
      });
    } finally {
      setPrivilegeDetailLoading(false);
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return <i className="fas fa-sort ms-1 text-muted"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fas fa-sort-up ms-1"></i>
      : <i className="fas fa-sort-down ms-1"></i>;
  };

  const toggleDropdown = (column) => {
    setOpenDropdown(openDropdown === column ? null : column);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.filter-dropdown')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  return (
    <div className="container-fluid" style={{ height: '100%', padding: '10px', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div className="row" style={{ height: '100%' }}>
        <div className="col-12" style={{ height: '100%' }}>
          <div className="card shadow-lg border-0" style={{ borderRadius: '15px', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="card-header py-3" style={{
              background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
              borderBottom: '2px solid #e2e8f0',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', width: '100%' }}>
                {/* Manager Attestations Title */}
                <h4 className="m-0 fw-bold" style={{ color: '#2d3748', flexShrink: 0 }}>
                  <i className="fas fa-clipboard-check me-2"></i>
                  Manager Attestations
                </h4>

                {/* Campaign Names Display */}
                {filterOptions.campaigns.length > 0 && (
                  <div className="d-flex align-items-center gap-2" style={{ flex: 1, marginLeft: '20px' }}>
                    <span style={{ color: '#718096', fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      <i className="fas fa-flag me-1"></i>Campaigns:
                    </span>
                    <div className="d-flex flex-wrap gap-2">
                      {filterOptions.campaigns.map(campaign => {
                        const campaignUsers = users.filter(u => u.campaign_name === campaign);
                        const pendingCount = campaignUsers.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending').length;
                        const isSelected = selectedCampaign === campaign;
                        const details = campaignDetails[campaign] || {};
                        const dueDate = details.attestation_deadline || details.end_date;
                        const dueDateFormatted = dueDate ? new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
                        return (
                          <div key={campaign} className="d-flex align-items-center gap-2">
                            <span
                              className="badge"
                              style={{
                                background: isSelected ? '#3182ce' : (pendingCount > 0 ? '#ed8936' : '#48bb78'),
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                padding: '6px 12px'
                              }}
                              onClick={() => {
                                if (selectedCampaign === campaign) {
                                  setSelectedCampaign(null);
                                  setSelectedPrivilegeLevel(null);
                                  setSelectedRiskLevel(null);
                                  setSelectedDatabases([]);
                                } else {
                                  setSelectedCampaign(campaign);
                                  setSelectedPrivilegeLevel(null);
                                  setSelectedRiskLevel(null);
                                  setSelectedDatabases([]);
                                }
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              title={`${campaign}\n${pendingCount} pending attestations\nClick to ${isSelected ? 'deselect' : 'select'}`}
                            >
                              {campaign}
                              {pendingCount > 0 && <span className="ms-1">({pendingCount})</span>}
                            </span>
                            {dueDateFormatted && (
                              <span style={{ fontSize: '0.75rem', color: '#718096', whiteSpace: 'nowrap' }}>
                                <i className="fas fa-calendar-alt me-1"></i>Due: {dueDateFormatted}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Bulk Delegation Button - Always visible on right */}
                <button
                  className="btn btn-outline-primary btn-sm ms-auto"
                  onClick={() => setShowDelegateModal(true)}
                  style={{ whiteSpace: 'nowrap' }}
                  data-tour="bulk-delegation"
                >
                  <i className="fas fa-users-cog me-1"></i>
                  Bulk Delegation
                </button>

                {/* Guide Me Button */}
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={startGuideMe}
                  style={{ whiteSpace: 'nowrap' }}
                  title="Start guided workflow"
                >
                  <i className="fas fa-compass me-1"></i>
                  Guide Me
                </button>
              </div>
            </div>
            <div className="card-body p-0" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Tab strip: Attestation (default decision-making UI) vs. User Access Report
                  (full per-technology access view, pre-filtered to the attester's assigned users). */}
              <div style={{
                display: 'flex',
                gap: 2,
                padding: '8px 12px 0 12px',
                background: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                flexShrink: 0
              }}>
                {[
                  { key: 'attestation', label: 'Attestation', icon: 'fa-clipboard-check' },
                  { key: 'accessReport', label: 'User Access Report', icon: 'fa-table' }
                ].map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveTab(t.key)}
                    style={{
                      padding: '8px 18px',
                      border: 'none',
                      borderBottom: activeTab === t.key ? '3px solid #3182ce' : '3px solid transparent',
                      background: activeTab === t.key ? '#fff' : 'transparent',
                      color: activeTab === t.key ? '#2d3748' : '#718096',
                      fontWeight: activeTab === t.key ? 600 : 500,
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className={`fas ${t.icon} me-2`} />{t.label}
                  </button>
                ))}
              </div>

              {activeTab === 'accessReport' ? (
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <UserAccessReportTab users={users} />
                </div>
              ) : (
              <>
              {/* Guide Me Header */}
              {guideMode && (
                <div className="p-3" style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderBottom: '1px solid #dee2e6'
                }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-compass me-2" style={{ fontSize: '1.3rem', color: 'white' }}></i>
                      <h6 className="mb-0 text-white">Guide Me: Attestation Workflow</h6>
                    </div>
                    <button
                      className="btn btn-outline-light btn-sm"
                      onClick={exitGuideMe}
                    >
                      <i className="fas fa-times me-1"></i>
                      Exit Guide
                    </button>
                  </div>

                  {/* Step Indicator */}
                  <div className="d-flex justify-content-between align-items-center" style={{ overflowX: 'auto' }}>
                    {guideSteps.map((step, index) => {
                      const currentStep = getCurrentGuideStep();
                      const isActive = step.step === currentStep;
                      const isCompleted = step.step < currentStep;

                      return (
                        <div key={step.step} className="d-flex align-items-center" style={{ flex: 1, minWidth: '80px' }}>
                          <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                            <div style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: isCompleted ? '#28a745' : isActive ? 'white' : 'rgba(255,255,255,0.3)',
                              color: isCompleted ? 'white' : isActive ? '#667eea' : 'rgba(255,255,255,0.7)',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              transition: 'all 0.3s ease',
                              boxShadow: isActive ? '0 0 0 3px rgba(255,255,255,0.4)' : 'none'
                            }}>
                              {isCompleted ? <i className="fas fa-check" style={{ fontSize: '0.65rem' }}></i> : step.step}
                            </div>
                            <small className="text-white mt-1" style={{
                              opacity: isActive ? 1 : 0.7,
                              fontWeight: isActive ? '600' : '400',
                              fontSize: '0.6rem',
                              textAlign: 'center',
                              lineHeight: '1.2',
                              maxWidth: '70px'
                            }}>
                              {step.title}
                            </small>
                          </div>
                          {index < guideSteps.length - 1 && (
                            <div style={{
                              height: '2px',
                              flex: 1,
                              minWidth: '10px',
                              background: isCompleted ? '#28a745' : 'rgba(255,255,255,0.3)',
                              margin: '0 2px',
                              marginBottom: '18px'
                            }}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Current Step Description */}
                  <div className="mt-2 p-2" style={{
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: '6px'
                  }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-white" style={{ opacity: 0.9 }}>
                          <strong>Step {getCurrentGuideStep()}:</strong> {guideSteps[getCurrentGuideStep() - 1]?.description}
                        </small>
                      </div>
                      {getCurrentGuideStep() >= 4 && getCurrentGuideStep() < 7 && (
                        <button
                          className="btn btn-sm btn-light ms-2"
                          onClick={advanceGuideStep}
                          style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                        >
                          Next <i className="fas fa-arrow-right ms-1"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'row' }}>
              {/* Left Sidebar - Hierarchical Drill-down */}
              <div style={{ width: '350px', minWidth: '350px', borderRight: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', background: '#f8f9fa', overflow: 'hidden' }} data-tour="sidebar">
                <div style={{ padding: '12px 15px', borderBottom: '1px solid #dee2e6', background: 'linear-gradient(135deg, #3182ce 0%, #2c5282 100%)', color: '#fff' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0" style={{ fontSize: '0.95rem', fontWeight: '600' }}><i className="fas fa-sitemap me-2"></i>Attestation Navigator</h6>
                    {!guideMode && (
                      <button
                        className="btn btn-outline-light btn-sm"
                        onClick={startGuideMe}
                        title="Start guided workflow"
                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                      >
                        <i className="fas fa-compass me-1"></i>
                        Guide Me
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '10px' }}>
                  {/* Breadcrumb Navigation */}
                  {(selectedCampaign || selectedPrivilegeLevel || selectedRiskLevel || selectedDatabases.length > 0) && (
                    <div style={{ marginBottom: '10px', padding: '8px', background: '#e2e8f0', borderRadius: '6px', fontSize: '0.75rem' }}>
                      <div className="d-flex align-items-center flex-wrap gap-1">
                        <button className="btn btn-link btn-sm p-0" onClick={() => { setSelectedCampaign(null); setSelectedPrivilegeLevel(null); setSelectedRiskLevel(null); setSelectedDatabases([]); }} style={{ fontSize: '0.75rem', textDecoration: 'none' }}><i className="fas fa-home"></i></button>
                        {selectedCampaign && (<><i className="fas fa-chevron-right text-muted" style={{ fontSize: '0.6rem' }}></i><button className="btn btn-link btn-sm p-0" onClick={() => { setSelectedPrivilegeLevel(null); setSelectedRiskLevel(null); setSelectedDatabases([]); }} style={{ fontSize: '0.75rem', textDecoration: 'none' }}>{selectedCampaign.length > 20 ? selectedCampaign.substring(0, 20) + '...' : selectedCampaign}</button></>)}
                        {selectedPrivilegeLevel && selectedRiskLevel && (<><i className="fas fa-chevron-right text-muted" style={{ fontSize: '0.6rem' }}></i><button className="btn btn-link btn-sm p-0" onClick={() => { setSelectedDatabases([]); }} style={{ fontSize: '0.75rem', textDecoration: 'none' }}>{selectedPrivilegeLevel} - {selectedRiskLevel}</button></>)}
                        {selectedDatabases.length > 0 && (<><i className="fas fa-chevron-right text-muted" style={{ fontSize: '0.6rem' }}></i><span style={{ fontWeight: '500' }}>{selectedDatabases.length === 1 ? selectedDatabases[0] : `${selectedDatabases.length} databases`}</span></>)}
                      </div>
                    </div>
                  )}

                  {/* Campaign List */}
                  <div className="mb-2" data-tour="campaign-list" style={guideMode && getCurrentGuideStep() === 1 ? {
                    border: '2px solid #667eea',
                    borderRadius: '8px',
                    padding: '10px',
                    background: 'rgba(102, 126, 234, 0.05)',
                    boxShadow: '0 0 15px rgba(102, 126, 234, 0.2)'
                  } : {}}>
                    {/* Guide Me Prompt - Step 1 */}
                    {guideMode && getCurrentGuideStep() === 1 && (
                      <div className="mb-2 p-2" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '6px',
                        color: 'white',
                        fontSize: '0.8rem'
                      }}>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-hand-pointer me-2"></i>
                          <div>
                            <strong>Step 1: {guideSteps[0].title}</strong>
                            <div style={{ opacity: 0.9, fontSize: '0.75rem' }}>{guideSteps[0].description}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    <h6 style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#718096', margin: '0 0 8px 0' }}>
                      <i className="fas fa-flag me-1"></i>Campaigns
                      {guideMode && getCurrentGuideStep() > 1 && <i className="fas fa-check-circle ms-2 text-success"></i>}
                    </h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {filterOptions.campaigns.map(campaign => {
                        const campaignUsersAll = users.filter(u => u.campaign_name === campaign);
                        // Apply the active view filter so the sidebar mirrors what the table will
                        // show. Otherwise users see "ADMIN: 1" but click and get an empty table
                        // because that ADMIN record is already attested and the table is on Pending.
                        const isPending = (u) => !u.attestation_decision || u.attestation_decision === 'Pending';
                        const campaignUsers = (viewFilter === 'pending')
                          ? campaignUsersAll.filter(isPending)
                          : (viewFilter === 'attested')
                            ? campaignUsersAll.filter(u => !isPending(u))
                            : campaignUsersAll;
                        const pendingCount = campaignUsersAll.filter(isPending).length;
                        // Skip campaigns that have no users for the current view (e.g. on Pending
                        // view, a fully-attested campaign disappears from the sidebar).
                        if (campaignUsers.length === 0) return null;
                        const isSelected = selectedCampaign === campaign;

                        // Get unique privilege levels for this campaign, sorted by matrix
                        // severity (highest first). Falls back to alphabetical if matrix not loaded.
                        const campaignTech = (campaignUsers[0]?.database_type || '').toLowerCase();
                        const campaignTiers = matrixTiersByTech[campaignTech] || [];
                        const tierValueMap = Object.fromEntries(campaignTiers.map(t => [t.tier_name, t.tier_value ?? 0]));
                        const campaignPrivilegeLevels = [...new Set(campaignUsers.map(u => u.privilege_level))]
                          .filter(Boolean)
                          .sort((a, b) => {
                            const va = tierValueMap[a];
                            const vb = tierValueMap[b];
                            if (va !== undefined && vb !== undefined) return vb - va; // severity desc
                            return a.localeCompare(b);
                          });

                        // Build grouped privilege-risk combinations (within the view-filtered set)
                        const privilegeRiskGroups = [];
                        const riskOrder = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
                        campaignPrivilegeLevels.forEach(level => {
                          const levelUsers = campaignUsers.filter(u => u.privilege_level === level);
                          const levelRiskLevels = [...new Set(levelUsers.map(u => u.risk_level))].filter(Boolean);
                          const sortedRiskLevels = levelRiskLevels.sort((a, b) => riskOrder.indexOf(a) - riskOrder.indexOf(b));
                          sortedRiskLevels.forEach(risk => {
                            const groupUsers = levelUsers.filter(u => u.risk_level === risk);
                            if (groupUsers.length === 0) return; // skip empty groups under the active view
                            const groupPendingCount = groupUsers.filter(isPending).length;
                            privilegeRiskGroups.push({
                              privilege: level,
                              risk: risk,
                              label: `${level} - ${risk}`,
                              userCount: groupUsers.length,
                              pendingCount: groupPendingCount
                            });
                          });
                        });

                        return (
                          <div key={campaign}>
                            <div onClick={() => { if (selectedCampaign === campaign) { setSelectedCampaign(null); setSelectedPrivilegeLevel(null); setSelectedRiskLevel(null); setSelectedDatabases([]); } else { setSelectedCampaign(campaign); setSelectedPrivilegeLevel(null); setSelectedRiskLevel(null); setSelectedDatabases([]); } }} style={{ padding: '10px 12px', borderRadius: '6px', cursor: 'pointer', background: isSelected ? '#3182ce' : '#fff', color: isSelected ? '#fff' : '#2d3748', border: isSelected ? '1px solid #2c5282' : '1px solid #e2e8f0', fontSize: '0.85rem', transition: 'all 0.2s' }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2"><i className={`fas fa-chevron-${isSelected ? 'down' : 'right'}`} style={{ fontSize: '0.7rem', width: '10px' }}></i><span style={{ fontWeight: '500' }}>{campaign}</span></div>
                                <span className="badge" style={{ background: isSelected ? 'rgba(255,255,255,0.3)' : (pendingCount > 0 ? '#ed8936' : '#48bb78'), color: '#fff', fontSize: '0.7rem' }}>{pendingCount > 0 ? pendingCount : campaignUsers.length}</span>
                              </div>
                            </div>
                            {isSelected && (
                              <div style={{
                                marginLeft: '15px',
                                marginTop: '4px',
                                borderLeft: '2px solid #e2e8f0',
                                paddingLeft: '10px',
                                ...(guideMode && getCurrentGuideStep() === 2 ? {
                                  border: '2px solid #667eea',
                                  borderRadius: '8px',
                                  padding: '10px',
                                  marginLeft: '5px',
                                  background: 'rgba(102, 126, 234, 0.05)',
                                  boxShadow: '0 0 15px rgba(102, 126, 234, 0.2)'
                                } : {})
                              }}>
                                {/* Guide Me Prompt - Step 2 */}
                                {guideMode && getCurrentGuideStep() === 2 && (
                                  <div className="mb-2 p-2" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '6px',
                                    color: 'white',
                                    fontSize: '0.8rem'
                                  }}>
                                    <div className="d-flex align-items-center">
                                      <i className="fas fa-hand-pointer me-2"></i>
                                      <div>
                                        <strong>Step 2: {guideSteps[1].title}</strong>
                                        <div style={{ opacity: 0.9, fontSize: '0.75rem' }}>{guideSteps[1].description}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {privilegeRiskGroups.map((group, groupIndex) => {
                                  const isGroupSelected = selectedPrivilegeLevel === group.privilege && selectedRiskLevel === group.risk;
                                  const levelStyle = getPrivilegeLevelStyle(group.privilege);
                                  const riskColors = { 'CRITICAL': '#e53e3e', 'HIGH': '#ed8936', 'MEDIUM': '#3182ce', 'LOW': '#48bb78' };

                                  // Get databases for this privilege-risk group
                                  const groupUsers = campaignUsers.filter(u => u.privilege_level === group.privilege && u.risk_level === group.risk);
                                  const groupDatabases = [...new Set(groupUsers.map(u => u.database_name))].filter(Boolean).sort();

                                  return (
                                    <div key={group.label}>
                                      <div
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isGroupSelected) {
                                            setSelectedPrivilegeLevel(null);
                                            setSelectedRiskLevel(null);
                                            setSelectedDatabases([]);
                                          } else {
                                            setSelectedPrivilegeLevel(group.privilege);
                                            setSelectedRiskLevel(group.risk);
                                            // Auto-select all databases when privilege-risk is selected
                                            setSelectedDatabases([...groupDatabases]);
                                          }
                                        }}
                                        style={{
                                          padding: '8px 10px',
                                          borderRadius: '5px',
                                          cursor: 'pointer',
                                          background: isGroupSelected ? levelStyle.background : '#f7fafc',
                                          color: isGroupSelected ? levelStyle.color : '#4a5568',
                                          border: isGroupSelected ? `1px solid ${levelStyle.background}` : '1px solid #e2e8f0',
                                          fontSize: '0.8rem',
                                          marginBottom: '3px',
                                          transition: 'all 0.2s'
                                        }}
                                        {...(groupIndex === 0 ? { 'data-tour': 'privilege-level-item' } : {})}
                                      >
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <i className={`fas fa-chevron-${isGroupSelected ? 'down' : 'right'}`} style={{ fontSize: '0.6rem', width: '8px' }}></i>
                                            <i className="fas fa-key" style={{ fontSize: '0.7rem' }}></i>
                                            <span style={{ fontWeight: '500' }}>{group.privilege}</span>
                                            <span style={{ color: isGroupSelected ? 'rgba(255,255,255,0.8)' : riskColors[group.risk], fontWeight: '600' }}>- {group.risk}</span>
                                          </div>
                                          <span className="badge" style={{ background: isGroupSelected ? 'rgba(255,255,255,0.3)' : (group.pendingCount > 0 ? '#ed8936' : '#718096'), color: '#fff', fontSize: '0.65rem' }}
                                            title={viewFilter === 'pending' ? `${group.pendingCount} pending` : viewFilter === 'attested' ? `${group.userCount} attested` : `${group.userCount} total (${group.pendingCount} pending)`}>
                                            {group.userCount}
                                          </span>
                                        </div>
                                      </div>
                                      {/* Database level - shown when privilege-risk is selected */}
                                      {isGroupSelected && (
                                        <div style={{
                                          marginLeft: '12px',
                                          marginTop: '3px',
                                          marginBottom: '6px',
                                          borderLeft: '2px solid #e2e8f0',
                                          paddingLeft: '8px',
                                          ...(guideMode && getCurrentGuideStep() === 3 ? {
                                            border: '2px solid #667eea',
                                            borderRadius: '8px',
                                            padding: '8px',
                                            marginLeft: '5px',
                                            background: 'rgba(102, 126, 234, 0.05)',
                                            boxShadow: '0 0 15px rgba(102, 126, 234, 0.2)'
                                          } : {})
                                        }} data-tour="database-list">
                                          {/* Guide Me Prompt - Step 3 */}
                                          {guideMode && getCurrentGuideStep() === 3 && (
                                            <div className="mb-2 p-2" style={{
                                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                              borderRadius: '6px',
                                              color: 'white',
                                              fontSize: '0.75rem'
                                            }}>
                                              <div className="d-flex align-items-center">
                                                <i className="fas fa-hand-pointer me-2"></i>
                                                <div>
                                                  <strong>Step 3: {guideSteps[2].title}</strong>
                                                  <div style={{ opacity: 0.9, fontSize: '0.7rem' }}>{guideSteps[2].description}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          {/* Select All Databases */}
                                          {groupDatabases.length > 1 && (
                                            <div
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (selectedDatabases.length === groupDatabases.length) {
                                                  setSelectedDatabases([]);
                                                } else {
                                                  setSelectedDatabases([...groupDatabases]);
                                                }
                                              }}
                                              style={{
                                                padding: '6px 10px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                background: selectedDatabases.length === groupDatabases.length ? '#3182ce' : '#edf2f7',
                                                color: selectedDatabases.length === groupDatabases.length ? '#fff' : '#4a5568',
                                                border: '1px solid #cbd5e0',
                                                fontSize: '0.75rem',
                                                marginBottom: '4px',
                                                transition: 'all 0.2s'
                                              }}
                                            >
                                              <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center gap-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedDatabases.length === groupDatabases.length}
                                                    onChange={() => {}}
                                                    style={{ cursor: 'pointer' }}
                                                  />
                                                  <span style={{ fontWeight: '600' }}>Select All Databases</span>
                                                </div>
                                                <span className="badge" style={{ background: selectedDatabases.length === groupDatabases.length ? 'rgba(255,255,255,0.3)' : '#718096', color: '#fff', fontSize: '0.6rem' }}>
                                                  {groupDatabases.length}
                                                </span>
                                              </div>
                                            </div>
                                          )}
                                          {groupDatabases.map(dbName => {
                                            const dbUsers = groupUsers.filter(u => u.database_name === dbName);
                                            const dbPendingCount = dbUsers.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending').length;
                                            const isDbSelected = selectedDatabases.includes(dbName);
                                            return (
                                              <div
                                                key={dbName}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (isDbSelected) {
                                                    setSelectedDatabases(selectedDatabases.filter(db => db !== dbName));
                                                  } else {
                                                    setSelectedDatabases([...selectedDatabases, dbName]);
                                                  }
                                                }}
                                                style={{
                                                  padding: '6px 10px',
                                                  borderRadius: '4px',
                                                  cursor: 'pointer',
                                                  background: isDbSelected ? '#4a5568' : '#fff',
                                                  color: isDbSelected ? '#fff' : '#4a5568',
                                                  border: isDbSelected ? '1px solid #2d3748' : '1px solid #e2e8f0',
                                                  fontSize: '0.75rem',
                                                  marginBottom: '2px',
                                                  transition: 'all 0.2s'
                                                }}
                                              >
                                                <div className="d-flex justify-content-between align-items-center">
                                                  <div className="d-flex align-items-center gap-2">
                                                    <input
                                                      type="checkbox"
                                                      checked={isDbSelected}
                                                      onChange={() => {}}
                                                      style={{ cursor: 'pointer' }}
                                                    />
                                                    <i className="fas fa-database" style={{ fontSize: '0.65rem' }}></i>
                                                    <span style={{ fontWeight: '500' }}>{dbName}</span>
                                                  </div>
                                                  <span className="badge" style={{ background: isDbSelected ? 'rgba(255,255,255,0.3)' : (dbPendingCount > 0 ? '#ed8936' : '#a0aec0'), color: '#fff', fontSize: '0.6rem' }}>
                                                    {dbPendingCount > 0 ? dbPendingCount : dbUsers.length}
                                                  </span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div style={{ padding: '10px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '15px' }} data-tour="summary-stats">
                    <h6 style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#718096', marginBottom: '8px' }}><i className="fas fa-chart-pie me-1"></i>{selectedCampaign ? 'Selection Summary' : 'Overall Summary'}</h6>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '0.75rem' }}>
                      <div><span className="text-muted">Total:</span> <strong>{stats.total_users}</strong></div>
                      <div><span className="text-muted">Pending:</span> <strong style={{ color: '#ed8936' }}>{stats.pending_decisions}</strong></div>
                      <div><span className="text-muted">Retained:</span> <strong style={{ color: '#48bb78' }}>{stats.completed_retained}</strong></div>
                      <div><span className="text-muted">Modified:</span> <strong style={{ color: '#805ad5' }}>{stats.completed_modified}</strong></div>
                      <div><span className="text-muted">Removed:</span> <strong style={{ color: '#e53e3e' }}>{stats.completed_removed}</strong></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Content - Main Table Area */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '15px' }}>
                {error && (
                  <StatusRibbon
                    tone="danger"
                    title={error}
                    onDismiss={() => setError(null)}
                  />
                )}

                {statusRibbon && (
                  <StatusRibbon
                    tone={statusRibbon.tone}
                    title={statusRibbon.title}
                    details={statusRibbon.details}
                    autoDismissMs={statusRibbon.autoDismissMs}
                    onDismiss={() => setStatusRibbon(null)}
                  />
                )}

                {!statusRibbon && successMessage && (
                  <StatusRibbon
                    tone="success"
                    title={successMessage}
                    onDismiss={() => setSuccessMessage(null)}
                    autoDismissMs={10000}
                  />
                )}

                {loading && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading attestation data...</p>
                  </div>
                )}

                {/* Welcome Panel - shown when no campaign is selected */}
                {!loading && users.length > 0 && !selectedCampaign && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                    <div style={{ textAlign: 'center', maxWidth: '500px' }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3182ce 0%, #805ad5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                        boxShadow: '0 4px 14px rgba(49, 130, 206, 0.3)'
                      }}>
                        <i className="fas fa-clipboard-check" style={{ fontSize: '2rem', color: '#fff' }}></i>
                      </div>
                      <h3 style={{ color: '#2d3748', marginBottom: '12px', fontWeight: '600' }}>Welcome to User Access Attestations</h3>
                      <p style={{ color: '#718096', fontSize: '1rem', lineHeight: '1.6', marginBottom: '24px' }}>
                        Use the sidebar on the left to select a campaign and start reviewing user access.
                        Need help getting started?
                      </p>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-primary btn-lg"
                          onClick={startGuideMe}
                          style={{
                            padding: '12px 32px',
                            fontSize: '1rem',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)'
                          }}
                        >
                          <i className="fas fa-compass me-2"></i>
                          Guide Me Through Attestation
                        </button>
                      </div>
                      <div style={{ marginTop: '16px' }}>
                        <button
                          className="btn btn-link"
                          onClick={() => setShowRiskLevelsModal(true)}
                          style={{ color: '#a0aec0', textDecoration: 'none' }}
                        >
                          <i className="fas fa-question-circle me-1"></i>
                          Learn about Risk Level Calculations
                        </button>
                      </div>
                      <p style={{ color: '#a0aec0', fontSize: '0.85rem', marginTop: '8px' }}>
                        <i className="fas fa-info-circle me-1"></i>
                        You can restart the guided tour anytime using the Guide Me button above
                      </p>
                    </div>
                  </div>
                )}

                {/* User List - shown when a campaign is selected (all users) or when privilege/risk/database is drilled into */}
                {!loading && users.length > 0 && selectedCampaign && (
                  <>
                    {/* Guide Me Prompts - Steps 4-7 */}
                    {guideMode && getCurrentGuideStep() >= 4 && (
                      <div className="mb-3 p-3" style={{
                        background: getCurrentGuideStep() === 7
                          ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                          : getCurrentGuideStep() === 6
                          ? 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)'
                          : getCurrentGuideStep() === 5
                          ? 'linear-gradient(135deg, #fd7e14 0%, #e55a00 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '8px',
                        color: 'white',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                      }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: 'rgba(255,255,255,0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '12px'
                            }}>
                              <i className={`fas ${
                                getCurrentGuideStep() === 4 ? 'fa-users' :
                                getCurrentGuideStep() === 5 ? 'fa-tasks' :
                                getCurrentGuideStep() === 6 ? 'fa-user-friends' :
                                'fa-chart-line'
                              }`} style={{ fontSize: '1.2rem' }}></i>
                            </div>
                            <div>
                              <strong style={{ fontSize: '1rem' }}>
                                Step {getCurrentGuideStep()}: {guideSteps[getCurrentGuideStep() - 1]?.title}
                              </strong>
                              <p className="mb-0" style={{ opacity: 0.9, fontSize: '0.85rem' }}>
                                {guideSteps[getCurrentGuideStep() - 1]?.description}
                              </p>
                              {getCurrentGuideStep() === 4 && (
                                <p className="mb-0 mt-1" style={{ opacity: 0.8, fontSize: '0.75rem' }}>
                                  <i className="fas fa-lightbulb me-1"></i>
                                  Tip: Click on a user row to expand and see detailed privilege information.
                                </p>
                              )}
                              {getCurrentGuideStep() === 5 && (
                                <p className="mb-0 mt-1" style={{ opacity: 0.8, fontSize: '0.75rem' }}>
                                  <i className="fas fa-lightbulb me-1"></i>
                                  Tip: Use the checkboxes to select multiple users for bulk actions.
                                </p>
                              )}
                              {getCurrentGuideStep() === 6 && (
                                <p className="mb-0 mt-1" style={{ opacity: 0.8, fontSize: '0.75rem' }}>
                                  <i className="fas fa-lightbulb me-1"></i>
                                  Tip: Click "Bulk Delegation" in the header or select users and use the delegate action.
                                </p>
                              )}
                              {getCurrentGuideStep() === 7 && (
                                <p className="mb-0 mt-1" style={{ opacity: 0.8, fontSize: '0.75rem' }}>
                                  <i className="fas fa-lightbulb me-1"></i>
                                  Tip: The progress bar shows how many users have been attested. Campaign completes automatically when all are done.
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            {getCurrentGuideStep() < 7 && (
                              <button
                                className="btn btn-light btn-sm"
                                onClick={advanceGuideStep}
                              >
                                Next <i className="fas fa-arrow-right ms-1"></i>
                              </button>
                            )}
                            {getCurrentGuideStep() === 7 && (
                              <button
                                className="btn btn-outline-light btn-sm"
                                onClick={exitGuideMe}
                              >
                                <i className="fas fa-check me-1"></i>
                                Done
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ flexShrink: 0 }}>
                    <div className="d-flex gap-2">
                      <div className="btn-group btn-group-sm" role="group" data-tour="view-filters">
                        <button
                          type="button"
                          className={`btn ${viewFilter === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setViewFilter('pending')}
                        >
                          <i className="fas fa-clock me-1"></i>
                          Pending Users
                        </button>
                        <button
                          type="button"
                          className={`btn ${viewFilter === 'attested' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setViewFilter('attested')}
                        >
                          <i className="fas fa-check-circle me-1"></i>
                          Attested Users
                        </button>
                        <button
                          type="button"
                          className={`btn ${viewFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setViewFilter('all')}
                        >
                          <i className="fas fa-list me-1"></i>
                          All Users
                        </button>
                      </div>
                      {selectedUsers.length > 0 && (
                        <span className="badge bg-primary" style={{ fontSize: '0.8rem' }}>
                          <i className="fas fa-check-square me-1"></i>
                          {selectedUsers.length} selected
                        </span>
                      )}
                      {(Object.values(filters).some(v => v) || sortConfig.key) && (
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={clearFilters}
                        >
                          <i className="fas fa-times me-1"></i>
                          Clear Filters & Sort
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div className="d-flex justify-content-between align-items-center mb-2 mt-2" style={{ flexShrink: 0 }}>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Showing {filteredUsers.length > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <label className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Per page:</label>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 'auto' }}
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={250}>250</option>
                        </select>
                      </div>
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-angle-double-left"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-angle-left"></i>
                      </button>
                      <span className="btn btn-outline-secondary disabled">
                        Page {currentPage} of {totalPages || 1}
                      </span>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        <i className="fas fa-angle-right"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        <i className="fas fa-angle-double-right"></i>
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive" style={{ flex: 1, overflow: 'auto' }} data-tour="user-table">
                    <table className="table table-hover table-striped table-sm" style={{ marginBottom: 0, fontSize: '0.8rem' }}>
                      <thead style={{
                        background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)'
                      }}>
                        <tr>
                          <th style={{ color: '#fff', verticalAlign: 'middle', whiteSpace: 'nowrap', width: '50px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={paginatedUsers.length > 0 && paginatedUsers.every(u =>
                                selectedUsers.includes(`${u.campaign_id}_${u.database_name}_${u.db_username}`)
                              )}
                              onChange={handleSelectAllUsers}
                              style={{ cursor: 'pointer' }}
                              title="Select all users on this page"
                            />
                          </th>
                          <th style={{ color: '#fff', verticalAlign: 'middle', whiteSpace: 'nowrap', cursor: 'pointer', padding: '6px 8px', fontSize: '0.75rem' }}
                              onClick={() => handleSort('attestation_decision')}>
                            Attestation Decision {getSortIcon('attestation_decision')}
                          </th>
                          <th style={{ color: '#fff', verticalAlign: 'middle', whiteSpace: 'nowrap', cursor: 'pointer', padding: '6px 8px', fontSize: '0.75rem' }}
                              onClick={() => handleSort('user_display_name')}>
                            Employee {getSortIcon('user_display_name')}
                          </th>
                          <th style={{ color: '#fff', verticalAlign: 'middle', whiteSpace: 'nowrap', cursor: 'pointer', padding: '6px 8px', fontSize: '0.75rem' }}
                              onClick={() => handleSort('db_username')}>
                            DB User Name {getSortIcon('db_username')}
                          </th>
                          <th style={{ color: '#fff', verticalAlign: 'middle', whiteSpace: 'nowrap', padding: '6px 8px', fontSize: '0.75rem' }}>
                            Privileges
                          </th>
                          <th style={{ color: '#fff', verticalAlign: 'middle', whiteSpace: 'nowrap', cursor: 'pointer', padding: '6px 8px', fontSize: '0.75rem' }}
                              onClick={() => handleSort('application_name')}>
                            Primary Application {getSortIcon('application_name')}
                          </th>
                          <th style={{ color: '#fff', verticalAlign: 'middle', whiteSpace: 'nowrap', padding: '6px 8px', fontSize: '0.75rem', maxWidth: '100px' }}>Comments</th>
                        </tr>
                        <tr style={{ background: 'rgba(255,255,255,0.1)' }}>
                          <th style={{ padding: '4px' }}></th>
                          {/* Decision Filter */}
                          <th style={{ padding: '4px' }}>
                            <div className="filter-dropdown" style={{ position: 'relative' }}>
                              <button
                                className="btn btn-sm btn-light dropdown-toggle w-100"
                                type="button"
                                onClick={() => toggleDropdown('attestation_decision')}
                                style={{ minWidth: '100px', textAlign: 'left', fontSize: '0.75rem', padding: '2px 6px' }}
                              >
                                {filters.attestation_decision.length > 0 ? `${filters.attestation_decision.length} selected` : 'All Decisions'}
                              </button>
                              {openDropdown === 'attestation_decision' && (
                                <ul className="dropdown-menu show" style={{ maxHeight: '300px', overflowY: 'auto', position: 'absolute', zIndex: 1000, display: 'block' }}>
                                  <li>
                                    <button className="dropdown-item" onClick={() => handleSelectAll('attestation_decision', filterOptions.attestation_decisions)}>
                                      <input type="checkbox" className="form-check-input me-2" checked={filters.attestation_decision.length === filterOptions.attestation_decisions.length} readOnly />
                                      <strong>Select All</strong>
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  {filterOptions.attestation_decisions.map(option => (
                                    <li key={option}>
                                      <button className="dropdown-item" onClick={() => handleFilterChange('attestation_decision', option)}>
                                        <input type="checkbox" className="form-check-input me-2" checked={filters.attestation_decision.includes(option)} readOnly />
                                        {option}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </th>
                          {/* Employee Filter */}
                          <th style={{ padding: '4px' }}>
                            <div className="filter-dropdown" style={{ position: 'relative' }}>
                              <button
                                className="btn btn-sm btn-light dropdown-toggle w-100"
                                type="button"
                                onClick={() => toggleDropdown('employee')}
                                style={{ minWidth: '100px', textAlign: 'left', fontSize: '0.75rem', padding: '2px 6px' }}
                              >
                                {filters.employee.length > 0 ? `${filters.employee.length} selected` : 'All Employees'}
                              </button>
                              {openDropdown === 'employee' && (
                                <ul className="dropdown-menu show" style={{ maxHeight: '300px', overflowY: 'auto', position: 'absolute', zIndex: 1000, display: 'block' }}>
                                  <li>
                                    <button className="dropdown-item" onClick={() => handleSelectAll('employee', filterOptions.employees)}>
                                      <input type="checkbox" className="form-check-input me-2" checked={filters.employee.length === filterOptions.employees.length} readOnly />
                                      <strong>Select All</strong>
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  {filterOptions.employees.map(option => (
                                    <li key={option}>
                                      <button className="dropdown-item" onClick={() => handleFilterChange('employee', option)}>
                                        <input type="checkbox" className="form-check-input me-2" checked={filters.employee.includes(option)} readOnly />
                                        {option}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </th>
                          {/* DB Username Filter */}
                          <th style={{ padding: '4px' }}>
                            <div className="filter-dropdown" style={{ position: 'relative' }}>
                              <button
                                className="btn btn-sm btn-light dropdown-toggle w-100"
                                type="button"
                                onClick={() => toggleDropdown('db_username')}
                                style={{ minWidth: '100px', textAlign: 'left', fontSize: '0.75rem', padding: '2px 6px' }}
                              >
                                {filters.db_username.length > 0 ? `${filters.db_username.length} selected` : 'All Users'}
                              </button>
                              {openDropdown === 'db_username' && (
                                <ul className="dropdown-menu show" style={{ maxHeight: '300px', overflowY: 'auto', position: 'absolute', zIndex: 1000, display: 'block' }}>
                                  <li>
                                    <button className="dropdown-item" onClick={() => handleSelectAll('db_username', filterOptions.db_usernames)}>
                                      <input type="checkbox" className="form-check-input me-2" checked={filters.db_username.length === filterOptions.db_usernames.length} readOnly />
                                      <strong>Select All</strong>
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  {filterOptions.db_usernames.map(option => (
                                    <li key={option}>
                                      <button className="dropdown-item" onClick={() => handleFilterChange('db_username', option)}>
                                        <input type="checkbox" className="form-check-input me-2" checked={filters.db_username.includes(option)} readOnly />
                                        {option}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </th>
                          {/* Privileges - no filter */}
                          <th style={{ padding: '4px' }}></th>
                          {/* Application Filter */}
                          <th style={{ padding: '4px' }}>
                            <div className="filter-dropdown" style={{ position: 'relative' }}>
                              <button
                                className="btn btn-sm btn-light dropdown-toggle w-100"
                                type="button"
                                onClick={() => toggleDropdown('application')}
                                style={{ minWidth: '100px', textAlign: 'left', fontSize: '0.75rem', padding: '2px 6px' }}
                              >
                                {filters.application.length > 0 ? `${filters.application.length} selected` : 'All'}
                              </button>
                              {openDropdown === 'application' && (
                                <ul className="dropdown-menu show" style={{ maxHeight: '300px', overflowY: 'auto', position: 'absolute', zIndex: 1000, display: 'block' }}>
                                  <li>
                                    <button className="dropdown-item" onClick={() => handleSelectAll('application', filterOptions.applications)}>
                                      <input type="checkbox" className="form-check-input me-2" checked={filters.application.length === filterOptions.applications.length} readOnly />
                                      <strong>Select All</strong>
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  {filterOptions.applications.map(option => (
                                    <li key={option}>
                                      <button className="dropdown-item" onClick={() => handleFilterChange('application', option)}>
                                        <input type="checkbox" className="form-check-input me-2" checked={filters.application.includes(option)} readOnly />
                                        {option}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </th>
                          {/* Comments - no filter */}
                          <th style={{ padding: '4px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.map((user, index) => {
                          const userKey = `${user.campaign_id}_${user.database_name}_${user.db_username}`;
                          const currentDecision = decisions[userKey]?.decision || '';
                          const currentComment = decisions[userKey]?.comment || '';

                          return (
                            <tr key={index}>
                              <td>
                                {!isGroupRecord(user) && (
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedUsers.includes(userKey)}
                                    onChange={() => handleSelectUser(userKey)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                )}
                              </td>
                              <td>
                                {isGroupRecord(user) && !user.group_can_attest ? (
                                  <div className="alert alert-info mb-0 p-2" style={{ fontSize: '0.8rem' }}>
                                    <i className="fas fa-info-circle me-1"></i>
                                    Attest all members first
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }} {...(index === 0 ? { 'data-tour': 'decision-icons' } : {})}>
                                    {/* Retain Icon Button */}
                                    {(() => {
                                      const isRetained = currentDecision === 'Retained' || (user.attestation_decision === 'Retained' && !currentDecision);
                                      const isDisabled = (user.attestation_decision === 'Retained' && !currentDecision) || (isGroupRecord(user) && !user.group_can_attest);
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => !isDisabled && handleDecisionChange(userKey, 'Retained', user)}
                                          disabled={isDisabled}
                                          title={isDisabled ? 'Already attested as Retained' : 'Retain access'}
                                          style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '6px',
                                            border: isRetained ? '2px solid #198754' : '1px solid #dee2e6',
                                            background: isRetained ? '#198754' : '#fff',
                                            color: isRetained ? '#fff' : '#198754',
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            opacity: isDisabled ? 0.6 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.15s ease'
                                          }}
                                        >
                                          <i className="fas fa-check" style={{ fontSize: '1rem' }}></i>
                                        </button>
                                      );
                                    })()}

                                    {/* Modify Icon Button - Hide for READ privilege level users and groups */}
                                    {user.privilege_level && user.privilege_level !== 'READ' && !isGroupRecord(user) && (() => {
                                      const isModified = currentDecision === 'Modify' || (user.attestation_decision === 'Modify' && !currentDecision);
                                      const isDisabled = user.attestation_decision === 'Modify' && !currentDecision;
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => !isDisabled && handleDecisionChange(userKey, 'Modify', user)}
                                          disabled={isDisabled}
                                          title={isDisabled ? 'Already attested as Modify' : 'Modify access level'}
                                          style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '6px',
                                            border: isModified ? '2px solid #ffc107' : '1px solid #dee2e6',
                                            background: isModified ? '#ffc107' : '#fff',
                                            color: isModified ? '#000' : '#ffc107',
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            opacity: isDisabled ? 0.6 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.15s ease'
                                          }}
                                        >
                                          <i className="fas fa-pen" style={{ fontSize: '0.9rem' }}></i>
                                        </button>
                                      );
                                    })()}

                                    {/* Remove Icon Button */}
                                    {(() => {
                                      const isRemoved = currentDecision === 'Removed' || ((user.attestation_decision === 'Removed' || user.attestation_decision === 'Rejected') && !currentDecision);
                                      const isDisabled = ((user.attestation_decision === 'Removed' || user.attestation_decision === 'Rejected') && !currentDecision) || (isGroupRecord(user) && !user.group_can_attest);
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => !isDisabled && handleDecisionChange(userKey, 'Removed', user)}
                                          disabled={isDisabled}
                                          title={isDisabled ? 'Already attested as Removed' : 'Remove access'}
                                          style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '6px',
                                            border: isRemoved ? '2px solid #dc3545' : '1px solid #dee2e6',
                                            background: isRemoved ? '#dc3545' : '#fff',
                                            color: isRemoved ? '#fff' : '#dc3545',
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            opacity: isDisabled ? 0.6 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.15s ease'
                                          }}
                                        >
                                          <i className="fas fa-times" style={{ fontSize: '1rem' }}></i>
                                        </button>
                                      );
                                    })()}

                                    {/* Delegate Icon Button */}
                                    {(() => {
                                      const isPending = !user.attestation_decision || user.attestation_decision === 'Pending';
                                      const isDisabled = !isPending || isGroupRecord(user);
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => !isDisabled && openUserDelegateModal(userKey)}
                                          disabled={isDisabled}
                                          title={isDisabled ? (isGroupRecord(user) ? 'Cannot delegate groups' : 'Already attested') : 'Delegate to another manager'}
                                          style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '6px',
                                            border: '1px solid #dee2e6',
                                            background: '#fff',
                                            color: '#6c757d',
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            opacity: isDisabled ? 0.4 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.15s ease'
                                          }}
                                        >
                                          <i className="fas fa-user-tag" style={{ fontSize: '0.9rem' }}></i>
                                        </button>
                                      );
                                    })()}
                                  </div>
                                )}
                                {/* Show saved attestation decision text */}
                                {user.attestation_decision && user.attestation_decision !== 'Pending' && (
                                  <div style={{
                                    marginTop: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    color: user.attestation_decision === 'Retained' ? '#198754' :
                                           user.attestation_decision === 'Modify' ? '#856404' :
                                           (user.attestation_decision === 'Removed' || user.attestation_decision === 'Rejected') ? '#dc3545' : '#6c757d'
                                  }}>
                                    <i className={`fas ${
                                      user.attestation_decision === 'Retained' ? 'fa-check-circle' :
                                      user.attestation_decision === 'Modify' ? 'fa-edit' :
                                      (user.attestation_decision === 'Removed' || user.attestation_decision === 'Rejected') ? 'fa-times-circle' : 'fa-circle'
                                    } me-1`}></i>
                                    {user.attestation_decision === 'Rejected' ? 'Removed' : user.attestation_decision}
                                  </div>
                                )}
                              </td>
                              <td>
                                {user.user_display_name && user.user_display_name !== 'Unknown User' ? (
                                  <>
                                    <strong>{user.user_display_name}</strong>
                                    <br />
                                    <small className="text-muted">{user.employee_id || '-'}</small>
                                  </>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <strong>{user.db_username}</strong>
                                    {isGroupRecord(user) && (
                                      <span className="badge bg-info ms-1" style={{ fontSize: '0.7rem' }}>
                                        <i className="fas fa-users me-1"></i>
                                        GROUP
                                      </span>
                                    )}
                                    {isGroupMember(user) && user.parent_group_name && (
                                      <span className="badge bg-secondary ms-1" style={{ fontSize: '0.7rem' }} title={`Member of ${user.parent_group_name}`}>
                                        <i className="fas fa-user-tag me-1"></i>
                                        VIA GROUP
                                      </span>
                                    )}
                                  </div>

                                  {/* MSSQL Principal Type indicator (SQL_LOGIN, WINDOWS_LOGIN, SQL_USER, etc.) or fallback to scope */}
                                  {(user.principal_type || user.principal_scope) && (
                                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                      <i className={`fas ${
                                        user.principal_type ? (
                                          user.principal_type.includes('LOGIN') ? 'fa-server' :
                                          user.principal_type.includes('GROUP') ? 'fa-users' : 'fa-database'
                                        ) : (
                                          user.principal_scope === 'SERVER' ? 'fa-server' : 'fa-database'
                                        )
                                      } me-1`}></i>
                                      {user.principal_type
                                        ? user.principal_type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
                                        : (user.principal_scope === 'SERVER' ? 'Server Login' : 'Database User')
                                      }
                                    </small>
                                  )}

                                  {/* Group Progress Bar */}
                                  {isGroupRecord(user) && (
                                    <div style={{ marginTop: '8px' }}>
                                      <div className="progress" style={{ height: '18px', marginBottom: '4px' }}>
                                        <div
                                          className={`progress-bar ${user.attestation_progress_pct === 100 ? 'bg-success' : 'bg-primary'}`}
                                          role="progressbar"
                                          style={{ width: `${user.attestation_progress_pct || 0}%` }}
                                          aria-valuenow={user.attestation_progress_pct || 0}
                                          aria-valuemin="0"
                                          aria-valuemax="100"
                                        >
                                          {user.attestation_progress_pct || 0}%
                                        </div>
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        {user.group_members_attested || 0} of {user.group_members_in_scope || 0} members attested
                                      </small>
                                      {user.group_attestation_rule && (
                                        <div className="mt-1">
                                          <span className={`badge ${user.attestation_decision === 'Retained' ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.7rem' }}>
                                            Auto: {user.group_attestation_rule}
                                          </span>
                                        </div>
                                      )}
                                      <div className="mt-2">
                                        <button
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => loadGroupMembers(user.campaign_id, user.db_username, user.campaign_name)}
                                          style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                        >
                                          <i className="fas fa-users me-1"></i>
                                          View Members
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Account Status and Last Login (for non-groups) */}
                                  {!isGroupRecord(user) && (
                                    <>
                                      <span
                                        style={{
                                          color: user.account_status === 'OPEN' ? '#38a169' : user.account_status === 'LOCKED' ? '#e57373' : '#ffb74d',
                                          fontSize: '0.75rem',
                                          fontWeight: '500'
                                        }}
                                        {...(index === 0 ? { 'data-tour': 'risk-badge' } : {})}
                                      >
                                        {user.account_status}
                                      </span>
                                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        {user.last_login_date ? (
                                          <>
                                            <i className="fas fa-clock me-1"></i>
                                            {new Date(user.last_login_date).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric'
                                            })}
                                          </>
                                        ) : (
                                          <>
                                            <i className="fas fa-times-circle me-1"></i>
                                            Never logged in
                                          </>
                                        )}
                                      </small>
                                      {isGroupMember(user) && user.parent_group_name && (
                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                          <i className="fas fa-sitemap me-1"></i>
                                          Group: {user.parent_group_name}
                                        </small>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                              {/* Privileges Column */}
                              <td>
                                {!isGroupRecord(user) ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleShowPrivilegeDetails(user)}
                                    title="View detailed privileges for this user"
                                    style={{ fontSize: '0.75rem', padding: '4px 8px', whiteSpace: 'nowrap' }}
                                    {...(index === 0 ? { 'data-tour': 'privilege-info' } : {})}
                                  >
                                    <i className="fas fa-shield-alt me-1"></i>
                                    Show Details
                                  </button>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>-</span>
                                )}
                              </td>
                              {/* Application Column */}
                              <td>
                                {user.application_name ? (
                                  <>
                                    <strong>{user.application_name}</strong>
                                    <br />
                                    <small className="text-muted">{user.application_id}</small>
                                  </>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Add comments..."
                                  value={currentComment}
                                  onChange={(e) => handleCommentChange(userKey, e.target.value)}
                                  style={{ minWidth: '200px' }}
                                  {...(index === 0 ? { 'data-tour': 'comments-field' } : {})}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom Pagination Controls */}
                  <div className="d-flex justify-content-between align-items-center mt-2" style={{ flexShrink: 0 }}>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Showing {filteredUsers.length > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                    </span>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-angle-double-left"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-angle-left"></i>
                      </button>
                      <span className="btn btn-outline-secondary disabled">
                        Page {currentPage} of {totalPages || 1}
                      </span>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        <i className="fas fa-angle-right"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        <i className="fas fa-angle-double-right"></i>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {!loading && users.length === 0 && !error && (
                <div className="alert alert-info text-center">
                  <i className="fas fa-info-circle me-2"></i>
                  No users found for attestation across all campaigns in ATTESTATION status
                </div>
              )}
              </div>
            </div>
              </>
              )}
          </div>
        </div>
        </div>
      </div>

      {/* Bulk Delegation Modal */}
      <WideModal
        show={showDelegateModal}
        onClose={() => { setShowDelegateModal(false); setDelegateSearchTerm(''); setDelegateAttester({ id: '', name: '', email: '' }); }}
        title="Bulk Delegation"
        headerIcon="fa-users-cog"
        headerClass="bg-primary text-white"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowDelegateModal(false);
                setDelegateAttester({ id: '', name: '', email: '' });
                setDelegateSearchTerm('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDelegate}
              disabled={!delegateAttester.id || loading}
            >
              {loading ? 'Delegating...' : 'Delegate All'}
            </button>
          </>
        }
      >
        {(() => {
          const allPendingCount = users.filter(u =>
            !u.attestation_decision || u.attestation_decision === 'Pending'
          ).length;

          return (
            <div className="alert alert-info mb-3">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Bulk Delegation</strong> will reassign <strong>all {allPendingCount} pending attestation(s)</strong> assigned to you to another manager.
              <br />
              <small className="text-muted">Already attested users will not be affected.</small>
            </div>
          );
        })()}
        <div className="mb-3">
          <label className="form-label fw-bold">Select Delegate Attester *</label>
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Search by name, email, employee ID, job title, or department..."
            value={delegateSearchTerm}
            onChange={(e) => setDelegateSearchTerm(e.target.value)}
            onFocus={loadEnterpriseUsers}
          />
          <select
            className="form-select"
            size="12"
            value={delegateAttester.id}
            onChange={(e) => {
              const selected = enterpriseUsers.find(u => u.user_id === e.target.value);
              if (selected) {
                setDelegateAttester({
                  id: selected.user_id,
                  name: selected.user_display_name,
                  email: selected.user_email
                });
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {enterpriseUsers
              .filter(entUser => {
                // Exclude the current logged-in user from the list
                if (entUser.user_email?.toLowerCase() === user?.email?.toLowerCase()) return false;
                // Exclude humans behind the pending accounts being bulk-delegated
                if (ownsAnyOfRows(entUser, users.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending'))) return false;
                if (!delegateSearchTerm) return true;
                const search = delegateSearchTerm.toLowerCase();
                return (
                  entUser.user_display_name?.toLowerCase().includes(search) ||
                  entUser.user_id?.toLowerCase().includes(search) ||
                  entUser.employee_id?.toLowerCase().includes(search) ||
                  entUser.user_email?.toLowerCase().includes(search) ||
                  entUser.job_title?.toLowerCase().includes(search) ||
                  entUser.department?.toLowerCase().includes(search)
                );
              })
              .map(entUser => (
                <option key={entUser.user_id} value={entUser.user_id}>
                  {entUser.employee_id} - ({entUser.user_display_name}, {entUser.job_title || 'N/A'}, {entUser.department || 'N/A'})
                </option>
              ))}
          </select>
          <small className="text-muted">
            {(() => {
              const availableUsers = enterpriseUsers.filter(u =>
                u.user_email?.toLowerCase() !== user?.email?.toLowerCase() &&
                !ownsAnyOfRows(u, users.filter(r => !r.attestation_decision || r.attestation_decision === 'Pending')));
              const filteredCount = delegateSearchTerm
                ? availableUsers.filter(u => {
                    const search = delegateSearchTerm.toLowerCase();
                    return u.user_display_name?.toLowerCase().includes(search) ||
                      u.user_id?.toLowerCase().includes(search) ||
                      u.employee_id?.toLowerCase().includes(search) ||
                      u.user_email?.toLowerCase().includes(search) ||
                      u.job_title?.toLowerCase().includes(search) ||
                      u.department?.toLowerCase().includes(search);
                  }).length
                : availableUsers.length;
              return delegateSearchTerm
                ? `${filteredCount} of ${availableUsers.length} users shown`
                : `${availableUsers.length} users available`;
            })()}
          </small>
        </div>
        {delegateAttester.id && (
          <div className="alert alert-info mb-0">
            <strong>Selected:</strong> {delegateAttester.name} ({delegateAttester.email})
          </div>
        )}
      </WideModal>

      {/* User Delegation Modal (for single user or selected users) */}
      <WideModal
        show={showUserDelegateModal}
        onClose={() => { setShowUserDelegateModal(false); setUsersToDelegateKeys([]); setDelegateSearchTerm(''); setDelegateAttester({ id: '', name: '', email: '' }); }}
        title={usersToDelegateKeys.length === 1 ? "Delegate User" : `Delegate ${usersToDelegateKeys.length} Users`}
        headerIcon="fa-user-tag"
        headerClass="bg-info text-white"
        maxWidth="800px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowUserDelegateModal(false);
                setUsersToDelegateKeys([]);
                setDelegateAttester({ id: '', name: '', email: '' });
                setDelegateSearchTerm('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-info text-white"
              onClick={handleUserDelegate}
              disabled={!delegateAttester.id || loading}
            >
              {loading ? 'Delegating...' : 'Delegate'}
            </button>
          </>
        }
      >
        {(() => {
          const usersToShow = usersToDelegateKeys
            .map(userKey => users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey))
            .filter(Boolean);
          const pendingCount = usersToShow.filter(u => !u.attestation_decision || u.attestation_decision === 'Pending').length;

          return (
            <>
              <div className="alert alert-light border mb-3">
                <strong><i className="fas fa-users me-2"></i>Users to delegate ({pendingCount} pending):</strong>
                <div style={{ maxHeight: '120px', overflowY: 'auto', marginTop: '8px' }}>
                  {usersToShow.map((u, idx) => (
                    <div key={idx} className="d-flex align-items-center gap-2 py-1">
                      <span className={`badge ${(!u.attestation_decision || u.attestation_decision === 'Pending') ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                        {u.attestation_decision || 'Pending'}
                      </span>
                      <span>{u.db_username}</span>
                      <small className="text-muted">({u.database_name})</small>
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        })()}
        <div className="mb-3">
          <label className="form-label fw-bold">Select Delegate Attester *</label>
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Search by name, email, employee ID, job title, or department..."
            value={delegateSearchTerm}
            onChange={(e) => setDelegateSearchTerm(e.target.value)}
            onFocus={loadEnterpriseUsers}
          />
          <select
            className="form-select"
            size="8"
            value={delegateAttester.id}
            onChange={(e) => {
              const selected = enterpriseUsers.find(u => u.user_id === e.target.value);
              if (selected) {
                setDelegateAttester({
                  id: selected.user_id,
                  name: selected.user_display_name,
                  email: selected.user_email
                });
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {enterpriseUsers
              .filter(entUser => {
                if (entUser.user_email?.toLowerCase() === user?.email?.toLowerCase()) return false;
                // Exclude humans behind the specific accounts being delegated
                if (ownsAnyOfRows(entUser, usersToDelegateKeys
                  .map(k => users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === k))
                  .filter(Boolean))) return false;
                if (!delegateSearchTerm) return true;
                const search = delegateSearchTerm.toLowerCase();
                return (
                  entUser.user_display_name?.toLowerCase().includes(search) ||
                  entUser.user_id?.toLowerCase().includes(search) ||
                  entUser.employee_id?.toLowerCase().includes(search) ||
                  entUser.user_email?.toLowerCase().includes(search) ||
                  entUser.job_title?.toLowerCase().includes(search) ||
                  entUser.department?.toLowerCase().includes(search)
                );
              })
              .map(entUser => (
                <option key={entUser.user_id} value={entUser.user_id}>
                  {entUser.employee_id} - ({entUser.user_display_name}, {entUser.job_title || 'N/A'}, {entUser.department || 'N/A'})
                </option>
              ))}
          </select>
        </div>
        {delegateAttester.id && (
          <div className="alert alert-success mb-0">
            <strong>Selected:</strong> {delegateAttester.name} ({delegateAttester.email})
          </div>
        )}
      </WideModal>

      {/* Privilege Details Modal */}
      {showPrivilegeDetailModal && ReactDOM.createPortal(
        <div
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setShowPrivilegeDetailModal(false)}
        >
          <div
            style={{ margin: 0, maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '2px solid #333' }}>
              <div className="uar-modal-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 className="uar-modal-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                  <i className="fas fa-shield-alt me-2"></i>
                  User Privilege Details
                </h5>
                <button
                  type="button"
                  onClick={() => setShowPrivilegeDetailModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.3)',
                    border: 'none',
                    borderRadius: '4px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    lineHeight: '1',
                    padding: '4px 12px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.5)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                >
                  ×
                </button>
              </div>
              <div className="uar-modal-body" style={{ padding: '20px', overflowY: 'auto', flex: '1 1 auto' }}>
                {privilegeDetailLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#667eea' }}></i>
                    <p style={{ marginTop: '15px', color: '#6c757d' }}>Loading privilege details...</p>
                  </div>
                ) : privilegeDetailData ? (
                  <div>
                    {/* User Header Information - Two Column Layout */}
                    <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                      <div className="row">
                        <div className="col-md-6" style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#667eea', fontWeight: '600', marginRight: '8px' }}>
                            <i className="fas fa-user me-1"></i>Database User:
                          </span>
                          <code style={{ fontSize: '1rem', fontWeight: '500' }}>{privilegeDetailData.db_username}</code>
                        </div>
                        <div className="col-md-6" style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#667eea', fontWeight: '600', marginRight: '8px' }}>
                            <i className="fas fa-database me-1"></i>Database:
                          </span>
                          <strong>{privilegeDetailData.database_name}</strong>
                          <small className="text-muted ms-1">({privilegeDetailData.database_type})</small>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <span style={{ color: '#667eea', fontWeight: '600', marginRight: '8px' }}>
                            <i className="fas fa-lock me-1"></i>Privilege Level:
                          </span>
                          <span className="badge" style={{
                            ...getPrivilegeLevelStyle(privilegeDetailData.privilege_level),
                            fontSize: '0.9rem',
                            padding: '4px 10px'
                          }}>
                            {privilegeDetailData.privilege_level || 'Unknown'}
                          </span>
                        </div>
                        <div className="col-md-6">
                          <span style={{ color: '#667eea', fontWeight: '600', marginRight: '8px' }}>
                            <i className="fas fa-clock me-1"></i>Data Collected:
                          </span>
                          {privilegeDetailData.user_info_collected_date ? (
                            <>
                              <strong>
                                {new Date(privilegeDetailData.user_info_collected_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </strong>
                              <small className="text-muted ms-1">
                                {new Date(privilegeDetailData.user_info_collected_date).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </small>
                            </>
                          ) : (
                            <span className="text-muted">Current</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {privilegeDetailData.error ? (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {privilegeDetailData.error}
                      </div>
                    ) : (
                      <>
                        {/* Roles Section */}
                        {privilegeDetailData.roles && privilegeDetailData.roles.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <h6 style={{ color: '#667eea', marginBottom: '15px', fontWeight: '600', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>
                              <i className="fas fa-user-shield me-2"></i>
                              Granted Roles ({privilegeDetailData.roles.length})
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered">
                                <thead style={{ background: '#f8f9fa' }}>
                                  <tr>
                                    <th>Role Name</th>
                                    <th>Admin Option</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {privilegeDetailData.roles.map((role, idx) => (
                                    <tr key={idx}>
                                      <td><code>{role.role_name || role}</code></td>
                                      <td>
                                        {role.admin_option === 'YES' ? (
                                          <span className="badge bg-warning">YES</span>
                                        ) : (
                                          <span className="badge bg-secondary">NO</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Privileges Section */}
                        {privilegeDetailData.privileges && privilegeDetailData.privileges.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <h6 style={{ color: '#667eea', marginBottom: '15px', fontWeight: '600', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>
                              <i className="fas fa-key me-2"></i>
                              Privileges ({privilegeDetailData.privileges.length})
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered">
                                <thead style={{ background: '#f8f9fa' }}>
                                  <tr>
                                    <th>Privilege</th>
                                    <th>Object/Table</th>
                                    <th>Source</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {privilegeDetailData.privileges.map((priv, idx) => (
                                    <tr key={idx}>
                                      <td><code>{priv.privilege || priv.name || priv}</code></td>
                                      <td>
                                        {priv.table ? (
                                          <code style={{ fontSize: '0.8rem' }}>{priv.table}</code>
                                        ) : (
                                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>System</span>
                                        )}
                                      </td>
                                      <td>
                                        <span className={`badge ${
                                          priv.source && priv.source.includes('Direct') ? 'bg-success' : 'bg-info'
                                        }`} style={{ fontSize: '0.75rem' }}>
                                          {priv.source || 'Direct'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* No Data Message */}
                        {(!privilegeDetailData.roles || privilegeDetailData.roles.length === 0) &&
                         (!privilegeDetailData.privileges || privilegeDetailData.privileges.length === 0) && (
                          <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            No detailed privilege information available for this user. The privilege level is calculated from collected data.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <i className="fas fa-exclamation-circle" style={{ fontSize: '2rem', color: '#dc3545' }}></i>
                    <p style={{ marginTop: '15px', color: '#6c757d' }}>Failed to load privilege details</p>
                  </div>
                )}
              </div>
              <div className="uar-modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #dee2e6', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPrivilegeDetailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modify Privilege Modal */}
      {showModifyModal && modifyModalUser && ReactDOM.createPortal(
        <div
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.7)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}
          onClick={handleCloseModifyModal}
        >
          <div
            style={{
              maxWidth: 600,
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#ffffff',
                borderRadius: 8,
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                border: '2px solid #333',
                overflow: 'hidden'
              }}>
              {/* Header */}
              <div style={{ flexShrink: 0, backgroundColor: '#ffc107', borderTopLeftRadius: 8, borderTopRightRadius: 8, padding: 20, borderBottom: '2px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 style={{ color: '#000', fontWeight: '600', margin: 0, fontSize: '1.25rem' }}>
                  <i className="fas fa-edit me-2"></i>
                  Modify Privilege Level
                </h5>
                <button
                  type="button"
                  onClick={handleCloseModifyModal}
                  aria-label="Close"
                  style={{ background: 'transparent', border: 'none', fontSize: '28px', fontWeight: '700', lineHeight: 1, color: '#000', cursor: 'pointer', padding: '0', opacity: 0.7 }}
                  onMouseOver={(e) => e.target.style.opacity = '1'}
                  onMouseOut={(e) => e.target.style.opacity = '0.7'}
                >×</button>
              </div>

              {/* Body */}
              <div style={{ overflowY: 'auto', flex: '1 1 auto', backgroundColor: '#ffffff', padding: 24 }}>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ color: '#212529', marginBottom: '8px' }}><strong>User:</strong> {modifyModalUser.db_username}</p>
                  <p style={{ color: '#212529', marginBottom: '8px' }}><strong>Database:</strong> {modifyModalUser.database_name}</p>
                  <p style={{ color: '#212529', marginBottom: '0' }}>
                    <strong>Current Privilege Level:</strong>{' '}
                    <span
                      style={{
                        ...getPrivilegeLevelStyle(modifyModalUser.privilege_level),
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        display: 'inline-block',
                        marginLeft: '4px'
                      }}
                    >
                      {modifyModalUser.privilege_level || 'Unknown'}
                    </span>
                  </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="targetPrivilegeLevel" style={{ color: '#212529', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                    Select Target Privilege Level: <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '8px' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    Modify privileges downward only. Select a lower privilege level to reduce user access.
                  </p>
                  <select
                    className="form-select"
                    id="targetPrivilegeLevel"
                    value={targetPrivilegeLevel}
                    onChange={(e) => setTargetPrivilegeLevel(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '1rem', color: '#212529', backgroundColor: '#fff' }}
                  >
                    <option value="">-- Select Lower Privilege Level --</option>
                    {getAvailablePrivilegeLevels(modifyModalUser.privilege_level, modifyModalUser.database_type).map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                  {getAvailablePrivilegeLevels(modifyModalUser.privilege_level, modifyModalUser.database_type).length === 0 && (
                    <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '12px', marginTop: '8px', color: '#856404' }}>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      User already has the lowest available privilege level for this technology. No modification available.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '0' }}>
                  <label htmlFor="modifyComment" style={{ color: '#212529', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                    Comments: <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '8px' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    Comments are mandatory for Modify decisions. Explain why the privilege level should be reduced.
                  </p>
                  <textarea
                    className="form-control"
                    id="modifyComment"
                    rows="4"
                    value={modifyComment}
                    onChange={(e) => setModifyComment(e.target.value)}
                    placeholder="Enter the reason for modifying this user's privilege level..."
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '1rem', color: '#212529', backgroundColor: '#fff', resize: 'vertical' }}
                  ></textarea>
                </div>
              </div>

              {/* Footer */}
              <div style={{ flexShrink: 0, backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6', padding: 16, borderBottomLeftRadius: 8, borderBottomRightRadius: 8, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleCloseModifyModal}
                  style={{ backgroundColor: '#6c757d', border: '1px solid #6c757d', borderRadius: '4px', color: '#fff', padding: '8px 16px', fontWeight: '500', cursor: 'pointer', fontSize: '1rem' }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmModify}
                  disabled={!targetPrivilegeLevel || !modifyComment.trim()}
                  style={{
                    backgroundColor: !targetPrivilegeLevel || !modifyComment.trim() ? '#6c757d' : '#ffc107',
                    border: '1px solid ' + (!targetPrivilegeLevel || !modifyComment.trim() ? '#6c757d' : '#ffc107'),
                    borderRadius: '4px',
                    color: '#000',
                    padding: '8px 16px',
                    fontWeight: '500',
                    cursor: !targetPrivilegeLevel || !modifyComment.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    opacity: !targetPrivilegeLevel || !modifyComment.trim() ? 0.6 : 1
                  }}
                  onMouseOver={(e) => {
                    if (targetPrivilegeLevel && modifyComment.trim()) {
                      e.target.style.backgroundColor = '#e0a800';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (targetPrivilegeLevel && modifyComment.trim()) {
                      e.target.style.backgroundColor = '#ffc107';
                    }
                  }}
                >
                  <i className="fas fa-edit me-1"></i>
                  Confirm Modification
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* System Account Warning Modal (Oracle maintained, Cassandra default, etc.) */}
      {showSystemAccountWarningModal && pendingSystemAccountDecision && ReactDOM.createPortal(
        <div
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={handleCancelSystemAccountWarning}
        >
          <div
            style={{ margin: 0, maxWidth: '600px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '3px solid #ffc107' }}>
              <div className="uar-modal-header" style={{ backgroundColor: '#ffc107', color: '#000', padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 className="uar-modal-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  System Account Warning
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelSystemAccountWarning}
                  aria-label="Close"
                ></button>
              </div>
              <div className="uar-modal-body" style={{ padding: '20px' }}>
                <div className="alert alert-warning mb-3">
                  <i className="fas fa-shield-alt me-2"></i>
                  <strong>This is a system/default account!</strong>
                </div>

                <p><strong>Account:</strong> <code>{pendingSystemAccountDecision.user.db_username}</code></p>
                <p><strong>Database:</strong> {pendingSystemAccountDecision.user.database_name}</p>
                <p><strong>Database Type:</strong> {pendingSystemAccountDecision.user.database_type}</p>
                <p><strong>Requested Action:</strong> <span className={`badge ${pendingSystemAccountDecision.decision === 'Removed' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                  {pendingSystemAccountDecision.decision}
                </span></p>

                <hr />

                <div className="mb-3">
                  <h6 className="text-danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    Important Warning:
                  </h6>
                  <ul className="text-muted">
                    <li>This is a system/default account required for database operations</li>
                    <li>{pendingSystemAccountDecision.decision === 'Removed'
                      ? 'Removing this account can cause severe database instability or failure'
                      : 'Modifying this account may cause unexpected system behavior'}</li>
                    <li>System accounts are automatically created and managed by the database platform</li>
                    <li>Changes to these accounts should only be made with explicit DBA approval</li>
                  </ul>
                </div>

                <div className="alert alert-danger mb-0">
                  <i className="fas fa-question-circle me-2"></i>
                  <strong>Are you sure you want to proceed with this action?</strong>
                  <p className="mb-0 mt-2 small">
                    By clicking "I Acknowledge and Proceed", you confirm that you have reviewed this warning
                    and have proper authorization to {pendingSystemAccountDecision.decision === 'Removed' ? 'remove' : 'modify'} this system account.
                  </p>
                </div>
              </div>
              <div className="uar-modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #dee2e6', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelSystemAccountWarning}
                >
                  <i className="fas fa-times me-1"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${pendingSystemAccountDecision.decision === 'Removed' ? 'btn-danger' : 'btn-warning'}`}
                  onClick={handleConfirmSystemAccountWarning}
                >
                  <i className="fas fa-check me-1"></i>
                  I Acknowledge and Proceed
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Self-Attestation Prevention Modal */}
      {showSelfAttestModal && selfAttestUser && ReactDOM.createPortal(
        <div
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => { setShowSelfAttestModal(false); setSelfAttestUser(null); }}
        >
          <div
            style={{ margin: 0, maxWidth: '550px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '3px solid #dc3545' }}>
              <div style={{ backgroundColor: '#dc3545', color: '#fff', padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h5 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                  <i className="fas fa-ban me-2"></i>
                  Self-Attestation Not Allowed
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => { setShowSelfAttestModal(false); setSelfAttestUser(null); }}
                  aria-label="Close"
                ></button>
              </div>
              <div style={{ padding: '24px' }}>
                <div className="alert alert-danger mb-3">
                  <i className="fas fa-user-shield me-2"></i>
                  <strong>You cannot attest your own account.</strong>
                </div>

                <p style={{ color: '#333', marginBottom: '12px' }}>
                  Compliance policy requires that attestation decisions are made by an independent reviewer.
                  You are identified as the owner of this account:
                </p>

                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', padding: '14px', marginBottom: '16px', border: '1px solid #dee2e6' }}>
                  <p className="mb-1"><strong>Account:</strong> <code>{selfAttestUser.db_username}</code></p>
                  <p className="mb-1"><strong>Database:</strong> {selfAttestUser.database_name}</p>
                  {selfAttestUser.user_email && (
                    <p className="mb-0"><strong>Email:</strong> {selfAttestUser.user_email}</p>
                  )}
                </div>

                <div className="alert alert-warning mb-0">
                  <i className="fas fa-hand-point-right me-2"></i>
                  <strong>Please delegate this attestation</strong> to another authorized reviewer using the
                  <em> Delegate Attester</em> feature for this account.
                </div>
              </div>
              <div style={{ padding: '16px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowSelfAttestModal(false); setSelfAttestUser(null); }}
                >
                  <i className="fas fa-check me-1"></i>
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Risk Detail Modal */}
      {/* eslint-disable no-undef */}
      {showRiskDetailModal && selectedUserForRiskDetail && ReactDOM.createPortal(
        <div
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.7)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowRiskDetailModal(false)}
        >
          <div
            style={{ margin: 0, maxWidth: '700px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              border: '2px solid #333'
            }}>
              {/* Header */}
              <div style={{
                flexShrink: 0,
                backgroundColor: selectedUserForRiskDetail.risk_level === 'CRITICAL' ? '#dc3545' :
                               selectedUserForRiskDetail.risk_level === 'HIGH' ? '#ffc107' :
                               selectedUserForRiskDetail.risk_level === 'MEDIUM' ? '#0dcaf0' : '#28a745',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                padding: '20px',
                borderBottom: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h5 style={{
                  color: selectedUserForRiskDetail.risk_level === 'HIGH' ? '#000' : '#fff',
                  fontWeight: '600',
                  margin: 0,
                  fontSize: '1.25rem'
                }}>
                  <i className={`fas ${
                    selectedUserForRiskDetail.risk_level === 'CRITICAL' ? 'fa-exclamation-triangle' :
                    selectedUserForRiskDetail.risk_level === 'HIGH' ? 'fa-exclamation-circle' :
                    selectedUserForRiskDetail.risk_level === 'MEDIUM' ? 'fa-info-circle' : 'fa-check-circle'
                  } me-2`}></i>
                  Risk Assessment Details
                </h5>
                <button
                  type="button"
                  onClick={() => setShowRiskDetailModal(false)}
                  style={{
                    background: selectedUserForRiskDetail.risk_level === 'HIGH' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)',
                    border: 'none',
                    borderRadius: '4px',
                    color: selectedUserForRiskDetail.risk_level === 'HIGH' ? '#000' : '#fff',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    lineHeight: '1',
                    padding: '4px 12px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = selectedUserForRiskDetail.risk_level === 'HIGH' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = selectedUserForRiskDetail.risk_level === 'HIGH' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)'}
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '24px'
              }}>
                {/* User Info - Compact Two Column Layout */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3" style={{ color: '#2d3748', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
                    <i className="fas fa-user me-2"></i>
                    User Information
                  </h6>
                  <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                    <div className="row">
                      <div className="col-md-6" style={{ marginBottom: '6px' }}>
                        <strong style={{ marginRight: '8px' }}>Database Username:</strong>
                        <code>{selectedUserForRiskDetail.db_username}</code>
                      </div>
                      <div className="col-md-6" style={{ marginBottom: '6px' }}>
                        <strong style={{ marginRight: '8px' }}>Database:</strong>
                        <span>{selectedUserForRiskDetail.database_name}</span>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <strong style={{ marginRight: '8px' }}>Enterprise User:</strong>
                        <span>{selectedUserForRiskDetail.user_display_name || 'Not Matched'}</span>
                      </div>
                      <div className="col-md-6">
                        <strong style={{ marginRight: '8px' }}>Department:</strong>
                        <span>{selectedUserForRiskDetail.user_department || selectedUserForRiskDetail.db_department || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Level */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3" style={{ color: '#2d3748', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Overall Risk Assessment
                  </h6>
                  <div className="card" style={{ border: '2px solid #e2e8f0', backgroundColor: '#f7fafc' }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Risk Level:</strong>
                        </div>
                        <span className={`badge ${
                          selectedUserForRiskDetail.risk_level === 'CRITICAL' ? 'bg-danger text-white' :
                          selectedUserForRiskDetail.risk_level === 'HIGH' ? 'bg-warning text-dark' :
                          selectedUserForRiskDetail.risk_level === 'MEDIUM' ? 'bg-info text-white' : 'bg-success text-white'
                        }`} style={{ fontSize: '1.2rem', padding: '10px 20px' }}>
                          <i className={`fas ${
                            selectedUserForRiskDetail.risk_level === 'CRITICAL' ? 'fa-exclamation-triangle' :
                            selectedUserForRiskDetail.risk_level === 'HIGH' ? 'fa-exclamation-circle' :
                            selectedUserForRiskDetail.risk_level === 'MEDIUM' ? 'fa-info-circle' : 'fa-check-circle'
                          } me-2`}></i>
                          {selectedUserForRiskDetail.risk_level || 'LOW'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Factors */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3" style={{ color: '#2d3748', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
                    <i className="fas fa-chart-line me-2"></i>
                    Contributing Risk Factors
                  </h6>

                  <div className="list-group">
                    {/* Privilege Level Factor */}
                    <div className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>
                            <i className="fas fa-shield-alt me-2" style={{
                              color: ['SUPER_ADMIN', 'ADMIN'].includes(selectedUserForRiskDetail.privilege_level) ? '#dc3545' :
                                     ['WRITE_ELEVATED', 'WRITE'].includes(selectedUserForRiskDetail.privilege_level) ? '#ffc107' : '#28a745'
                            }}></i>
                            Privilege Level
                          </strong>
                          <br/>
                          <small className="text-muted">Level of access permissions granted</small>
                        </div>
                        <span className="badge" style={{...getPrivilegeLevelStyle(selectedUserForRiskDetail.privilege_level)}}>
                          {selectedUserForRiskDetail.privilege_level}
                        </span>
                      </div>
                    </div>

                    {/* Account Status Factor */}
                    <div className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>
                            <i className={`fas fa-user-shield me-2 ${selectedUserForRiskDetail.account_status === 'OPEN' ? 'text-danger' : 'text-success'}`}></i>
                            Account Status
                          </strong>
                          <br/>
                          <small className="text-muted">Active accounts pose higher risk</small>
                        </div>
                        <span className={`badge ${selectedUserForRiskDetail.account_status === 'OPEN' ? 'bg-danger' : 'bg-success'}`}>
                          {selectedUserForRiskDetail.account_status || 'Unknown'}
                        </span>
                      </div>
                    </div>

                    {/* Last Login Factor */}
                    <div className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>
                            <i className="fas fa-clock me-2 text-info"></i>
                            Last Login Activity
                          </strong>
                          <br/>
                          <small className="text-muted">Recent activity increases security priority</small>
                        </div>
                        <span className="text-muted">
                          {selectedUserForRiskDetail.db_last_updated ? new Date(selectedUserForRiskDetail.db_last_updated).toLocaleDateString() : 'Never / Unknown'}
                        </span>
                      </div>
                    </div>

                    {/* Proxy Grant Factor */}
                    <div className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>
                            <i className={`fas fa-users-cog me-2 ${selectedUserForRiskDetail.has_proxy_grant ? 'text-danger' : 'text-success'}`}></i>
                            Proxy Grant Privilege
                          </strong>
                          <br/>
                          <small className="text-muted">Ability to authenticate as other users</small>
                        </div>
                        <span className={`badge ${selectedUserForRiskDetail.has_proxy_grant ? 'bg-danger' : 'bg-success'}`}>
                          <i className={`fas ${selectedUserForRiskDetail.has_proxy_grant ? 'fa-exclamation-triangle' : 'fa-check'} me-1`}></i>
                          {selectedUserForRiskDetail.has_proxy_grant ? 'Yes - High Risk' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Explanation */}
                <div className={`alert ${
                  selectedUserForRiskDetail.risk_level === 'CRITICAL' ? 'alert-danger' :
                  selectedUserForRiskDetail.risk_level === 'HIGH' ? 'alert-warning' :
                  selectedUserForRiskDetail.risk_level === 'MEDIUM' ? 'alert-info' : 'alert-success'
                }`}>
                  <h6 className="alert-heading">
                    <i className="fas fa-info-circle me-2"></i>
                    Risk Level Explanation
                  </h6>
                  <p className="mb-0">
                    {selectedUserForRiskDetail.risk_level === 'CRITICAL' &&
                      'This account has been classified as CRITICAL risk due to a combination of high privilege level (SUPER_ADMIN or ADMIN), active account status, and potentially recent activity or proxy grant privileges. Immediate review and attestation is required.'}
                    {selectedUserForRiskDetail.risk_level === 'HIGH' &&
                      'This account has been classified as HIGH risk due to elevated privileges and/or active status. The combination of privilege level, account status, and usage patterns indicates significant security considerations that require careful review.'}
                    {selectedUserForRiskDetail.risk_level === 'MEDIUM' &&
                      'This account has been classified as MEDIUM risk. While it has some elevated privileges or concerning factors, the overall risk profile is moderate. Review is recommended to ensure access remains appropriate.'}
                    {selectedUserForRiskDetail.risk_level === 'LOW' &&
                      'This account has been classified as LOW risk due to limited privileges, inactive status, or lack of recent activity. Routine review is sufficient for accounts in this category.'}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                flexShrink: 0,
                padding: '16px 24px',
                borderTop: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRiskDetailModal(false)}
                >
                  <i className="fas fa-times me-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* eslint-enable no-undef */}

      {/* Group Members Modal */}
      {showGroupMembersModal && selectedGroup && ReactDOM.createPortal(
        <div
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.7)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowGroupMembersModal(false)}
        >
          <div
            style={{
              margin: 0,
              maxWidth: '95vw',
              width: '95vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}>
              <div className="modal-header bg-primary text-white" style={{ flexShrink: 0 }}>
                <div style={{ flex: 1 }}>
                  <h4 className="modal-title mb-2">
                    <i className="fas fa-users me-2"></i>
                    Group Attestation: {selectedGroup.group_name}
                  </h4>
                  <div className="mb-2">
                    {(() => {
                      const attestedCount = groupMembers.filter(m => m.attestation_decision !== null).length;
                      const totalCount = groupMembers.length;
                      const percentage = totalCount > 0 ? Math.round((attestedCount / totalCount) * 100) : 0;
                      return (
                        <>
                          <div className="progress" style={{ height: '8px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{ width: `${percentage}%` }}
                              aria-valuenow={percentage}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                          <small style={{ fontSize: '0.85rem', opacity: 0.95 }}>
                            Progress: {attestedCount} of {totalCount} members attested ({percentage}%)
                          </small>
                        </>
                      );
                    })()}
                  </div>
                  <p className="mb-0" style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                    Attest each member individually. The group will be automatically attested when all members are complete.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowGroupMembersModal(false)}
                  style={{ marginLeft: '1rem' }}
                ></button>
              </div>

              <div className="modal-body" style={{ padding: '0', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {loadingGroupMembers ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading group members...</p>
                  </div>
                ) : (
                  <div style={{ flex: 1, overflow: 'auto', padding: '1rem', width: '100%' }}>
                    <table className="table table-hover table-bordered mb-0" style={{ fontSize: '0.9rem', width: '100%', tableLayout: 'auto' }}>
                      <thead className="table-dark sticky-top" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap', width: '200px' }}>Action</th>
                          <th style={{ padding: '12px 10px', whiteSpace: 'nowrap', width: '120px' }}>Employee ID</th>
                          <th style={{ padding: '12px 10px', whiteSpace: 'nowrap', width: '180px' }}>Name</th>
                          <th style={{ padding: '12px 10px', whiteSpace: 'nowrap', width: '150px' }}>Department</th>
                          <th style={{ padding: '12px 10px', whiteSpace: 'nowrap', width: '180px' }}>Job Title</th>
                          <th style={{ padding: '12px 10px', whiteSpace: 'nowrap', width: '140px' }}>Privilege Level</th>
                          <th style={{ padding: '12px 10px', whiteSpace: 'nowrap', width: '110px' }}>Status</th>
                          <th style={{ padding: '12px 10px', whiteSpace: 'nowrap', width: '120px' }}>Attested On</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupMembers.map((member) => {
                          const isAttested = member.attestation_decision !== null;
                          const rowClass = isAttested ? 'table-success' : '';

                          return (
                            <tr key={member.report_id} className={rowClass}>
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                {!isAttested ? (
                                  attestingMemberId === member.report_id ? (
                                    <div className="text-center">
                                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Saving...</span>
                                      </div>
                                      <small className="d-block text-muted mt-1">Saving...</small>
                                    </div>
                                  ) : (
                                    <div className="d-flex justify-content-center gap-4">
                                      <label className="d-flex align-items-center" style={{ cursor: 'pointer', marginBottom: 0 }}>
                                        <input
                                          type="radio"
                                          name={`member-${member.report_id}`}
                                          value="Retained"
                                          onChange={() =>
                                            handleGroupMemberAttestation(
                                              { ...member, campaign_id: selectedGroup.campaign_id, campaign_name: selectedGroup.campaign_name || '' },
                                              'Retained',
                                              'Approved via group attestation'
                                            )
                                          }
                                          disabled={attestingMemberId !== null}
                                          style={{ marginRight: '6px', cursor: attestingMemberId !== null ? 'not-allowed' : 'pointer' }}
                                        />
                                        <span className="text-success fw-bold">
                                          <i className="fas fa-check me-1"></i>
                                          Retain
                                        </span>
                                      </label>
                                      <label className="d-flex align-items-center" style={{ cursor: 'pointer', marginBottom: 0 }}>
                                        <input
                                          type="radio"
                                          name={`member-${member.report_id}`}
                                          value="Removed"
                                          onChange={() =>
                                            handleGroupMemberAttestation(
                                              { ...member, campaign_id: selectedGroup.campaign_id, campaign_name: selectedGroup.campaign_name || '' },
                                              'Removed',
                                              'Removed via group attestation'
                                            )
                                          }
                                          disabled={attestingMemberId !== null}
                                          style={{ marginRight: '6px', cursor: attestingMemberId !== null ? 'not-allowed' : 'pointer' }}
                                        />
                                        <span className="text-danger fw-bold">
                                          <i className="fas fa-times me-1"></i>
                                          Remove
                                        </span>
                                      </label>
                                    </div>
                                  )
                                ) : (
                                  <div className="text-center">
                                    <span
                                      className={`badge ${
                                        member.attestation_decision === 'Retained'
                                          ? 'bg-success'
                                          : 'bg-danger'
                                      }`}
                                      style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                                    >
                                      <i className={`fas ${member.attestation_decision === 'Retained' ? 'fa-check' : 'fa-times'} me-1`}></i>
                                      {member.attestation_decision}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '10px' }}>
                                <strong>{member.db_username}</strong>
                              </td>
                              <td style={{ padding: '10px' }}>{member.user_display_name || '-'}</td>
                              <td style={{ padding: '10px' }}>{member.user_department || '-'}</td>
                              <td style={{ padding: '10px' }}>
                                {member.user_job_title || '-'}
                              </td>
                              <td style={{ padding: '10px' }}>
                                <span
                                  className="badge"
                                  style={getPrivilegeLevelStyle(member.privilege_level)}
                                >
                                  {member.privilege_level}
                                </span>
                              </td>
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                {isAttested ? (
                                  <span className="badge bg-success">
                                    <i className="fas fa-check me-1"></i>
                                    Attested
                                  </span>
                                ) : (
                                  <span className="badge bg-warning text-dark">
                                    <i className="fas fa-clock me-1"></i>
                                    Pending
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                {isAttested && member.attestation_date ? (
                                  <span>
                                    {new Date(member.attestation_date).toLocaleDateString()}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{
                flexShrink: 0,
                padding: '16px 24px',
                borderTop: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>Progress:</strong>{' '}
                  {groupMembers.filter((m) => m.attestation_decision).length} of{' '}
                  {groupMembers.length} members attested
                  {groupMembers.length > 0 && (
                    <span className="ms-2">
                      (
                      {Math.round(
                        (groupMembers.filter((m) => m.attestation_decision).length /
                          groupMembers.length) *
                          100
                      )}
                      %)
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowGroupMembersModal(false)}
                >
                  <i className="fas fa-times me-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Risk Levels Explanation Modal */}
      {showRiskLevelsModal && ReactDOM.createPortal(
        <div
          style={{
            backgroundColor: 'rgba(0,0,0,0.7)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowRiskLevelsModal(false)}
        >
          <div
            style={{ margin: 0, maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              border: '2px solid #333'
            }}>
              {/* Header */}
              <div style={{
                flexShrink: 0,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px',
                padding: '20px 24px',
                borderBottom: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h5 style={{ color: '#fff', fontWeight: '600', margin: 0, fontSize: '1.25rem' }}>
                  <i className="fas fa-calculator me-2"></i>
                  Understanding Risk Level Calculation
                </h5>
                <button
                  type="button"
                  onClick={() => setShowRiskLevelsModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.3)',
                    border: 'none',
                    borderRadius: '4px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    lineHeight: '1',
                    padding: '4px 12px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.5)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                {/* Introduction */}
                <div style={{ marginBottom: '24px', padding: '16px', background: '#f7fafc', borderRadius: '8px', borderLeft: '4px solid #3182ce' }}>
                  <p style={{ margin: 0, color: '#4a5568', fontSize: '0.95rem', lineHeight: '1.6' }}>
                    <i className="fas fa-info-circle me-2" style={{ color: '#3182ce' }}></i>
                    Risk levels are automatically calculated based on multiple factors including privilege level, account status, login activity, and enterprise user status.
                    Higher risk users require more careful review during attestation.
                  </p>
                </div>

                {/* Factors Section */}
                <div style={{ marginBottom: '24px' }}>
                  <h6 style={{ color: '#2d3748', marginBottom: '16px', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px', fontWeight: '600' }}>
                    <i className="fas fa-cogs me-2" style={{ color: '#805ad5' }}></i>
                    Factors Used in Risk Calculation
                  </h6>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                      <i className="fas fa-key" style={{ fontSize: '1.5rem', color: '#805ad5', marginBottom: '8px' }}></i>
                      <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>Privilege Level</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>READ to SUPER_ADMIN</div>
                    </div>
                    <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                      <i className="fas fa-user-shield" style={{ fontSize: '1.5rem', color: '#3182ce', marginBottom: '8px' }}></i>
                      <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>Account Status</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>Active, Locked, Expired</div>
                    </div>
                    <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                      <i className="fas fa-clock" style={{ fontSize: '1.5rem', color: '#38a169', marginBottom: '8px' }}></i>
                      <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>Last Login</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>Days since activity</div>
                    </div>
                    <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                      <i className="fas fa-building" style={{ fontSize: '1.5rem', color: '#d69e2e', marginBottom: '8px' }}></i>
                      <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>Enterprise Status</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>Account enabled/disabled</div>
                    </div>
                    <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                      <i className="fas fa-user-cog" style={{ fontSize: '1.5rem', color: '#e53e3e', marginBottom: '8px' }}></i>
                      <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>Special Privileges</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>DBA, SYSDBA roles</div>
                    </div>
                    <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                      <i className="fas fa-link" style={{ fontSize: '1.5rem', color: '#319795', marginBottom: '8px' }}></i>
                      <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>User Matching</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>DB to Enterprise mapping</div>
                    </div>
                  </div>
                </div>

                {/* Risk Level Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  {/* CRITICAL */}
                  <div style={{ padding: '16px', background: '#fff5f5', borderRadius: '10px', border: '2px solid #fc8181', borderLeft: '6px solid #dc3545' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span className="badge bg-danger" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                        <i className="fas fa-exclamation-triangle me-1"></i> CRITICAL
                      </span>
                    </div>
                    <h6 style={{ color: '#c53030', marginBottom: '10px', fontWeight: '600' }}>Super Admin / DBA Level Access</h6>
                    <ul style={{ fontSize: '0.85rem', color: '#742a2a', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Privilege Level 6 (SUPER_ADMIN)</li>
                      <li>Has DBA or SYSDBA privileges</li>
                      <li>Full system control access</li>
                      <li>Can modify system configuration</li>
                      <li>Highest priority for review</li>
                    </ul>
                  </div>

                  {/* HIGH */}
                  <div style={{ padding: '16px', background: '#fffbeb', borderRadius: '10px', border: '2px solid #fcd34d', borderLeft: '6px solid #ffc107' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span className="badge bg-warning text-dark" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                        <i className="fas fa-exclamation-circle me-1"></i> HIGH
                      </span>
                    </div>
                    <h6 style={{ color: '#92400e', marginBottom: '10px', fontWeight: '600' }}>Admin or Inactive Accounts</h6>
                    <ul style={{ fontSize: '0.85rem', color: '#78350f', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Privilege Level 5 (ADMIN)</li>
                      <li>No login for 90+ days</li>
                      <li>Account locked/expired with elevated privileges</li>
                      <li>Enterprise user account disabled</li>
                      <li>Potential security risk from inactivity</li>
                    </ul>
                  </div>

                  {/* MEDIUM */}
                  <div style={{ padding: '16px', background: '#e0f7fa', borderRadius: '10px', border: '2px solid #4dd0e1', borderLeft: '6px solid #17a2b8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span className="badge bg-info" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                        <i className="fas fa-info-circle me-1"></i> MEDIUM
                      </span>
                    </div>
                    <h6 style={{ color: '#0c5460', marginBottom: '10px', fontWeight: '600' }}>Elevated Privileges</h6>
                    <ul style={{ fontSize: '0.85rem', color: '#155724', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Privilege Level 3-4 (WRITE/WRITE_ELEVATED)</li>
                      <li>No login for 30-90 days</li>
                      <li>Can modify data or schema</li>
                      <li>Requires periodic verification</li>
                      <li>Monitor for unusual activity</li>
                    </ul>
                  </div>

                  {/* LOW */}
                  <div style={{ padding: '16px', background: '#f0fff4', borderRadius: '10px', border: '2px solid #9ae6b4', borderLeft: '6px solid #28a745' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span className="badge bg-success" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                        <i className="fas fa-check-circle me-1"></i> LOW
                      </span>
                    </div>
                    <h6 style={{ color: '#276749', marginBottom: '10px', fontWeight: '600' }}>Standard User Access</h6>
                    <ul style={{ fontSize: '0.85rem', color: '#22543d', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Privilege Level 1-2 (READ/READ_ELEVATED)</li>
                      <li>Regular login activity</li>
                      <li>Active enterprise user account</li>
                      <li>Standard access patterns</li>
                      <li>Lower priority for review</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                flexShrink: 0,
                padding: '16px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                background: '#f7fafc',
                borderBottomLeftRadius: '10px',
                borderBottomRightRadius: '10px'
              }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowRiskLevelsModal(false)}
                >
                  <i className="fas fa-check me-2"></i>
                  Got It
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Floating Bulk Action Bar */}
      {selectedUsers.length > 0 && (() => {
        // Count pending (un-attested) users in selection
        const pendingSelectedCount = selectedUsers.filter(userKey => {
          const user = users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey);
          return user && (!user.attestation_decision || user.attestation_decision === 'Pending');
        }).length;

        const handleDragStart = (e) => {
          // Only start drag from the drag handle
          if (e.target.closest('[data-drag-handle]')) {
            setIsDragging(true);
            const bar = e.currentTarget;
            const rect = bar.getBoundingClientRect();
            setDragOffset({
              x: e.clientX - rect.left,
              y: e.clientY - rect.top
            });
            e.preventDefault();
          }
        };

        return ReactDOM.createPortal(
          <div
            data-tour="bulk-actions"
            onMouseDown={handleDragStart}
            style={{
              position: 'fixed',
              ...(floatingBarPosition.x !== null ? {
                left: floatingBarPosition.x + 'px',
                top: floatingBarPosition.y + 'px',
                transform: 'none'
              } : {
                bottom: '30px',
                left: '50%',
                transform: 'translateX(-50%)'
              }),
              zIndex: 9999,
              background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
              borderRadius: '12px',
              padding: '12px 20px',
              boxShadow: isDragging ? '0 12px 40px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: floatingBarPosition.x === null ? 'slideUp 0.3s ease-out' : 'none',
              cursor: isDragging ? 'grabbing' : 'default',
              userSelect: 'none'
            }}
          >
            <style>{`
              @keyframes slideUp {
                from { opacity: 0; transform: translateX(-50%) translateY(20px); }
                to { opacity: 1; transform: translateX(-50%) translateY(0); }
              }
            `}</style>

            {/* Drag Handle */}
            <div
              data-drag-handle
              style={{
                cursor: isDragging ? 'grabbing' : 'grab',
                padding: '4px 8px',
                marginRight: '4px',
                borderRight: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}
              title="Drag to move"
            >
              <div style={{ display: 'flex', gap: '2px' }}>
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }}></div>
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }}></div>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }}></div>
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }}></div>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }}></div>
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }}></div>
              </div>
            </div>

            {/* Selection Count */}
            <div style={{
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: '500',
              borderRight: '1px solid rgba(255,255,255,0.2)',
              paddingRight: '12px'
            }}>
              <i className="fas fa-check-square me-2" style={{ color: '#63b3ed' }}></i>
              {selectedUsers.length} selected
              {pendingSelectedCount < selectedUsers.length && (
                <span style={{ color: '#a0aec0', fontSize: '0.75rem', marginLeft: '4px' }}>
                  ({selectedUsers.length - pendingSelectedCount} attested)
                </span>
              )}
            </div>

            {/* Action Buttons */}
            <button
              className="btn btn-success btn-sm"
              onClick={() => handleBulkDecision('Retained')}
              disabled={pendingSelectedCount === 0}
              style={{
                padding: '8px 16px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                fontWeight: '500',
                opacity: pendingSelectedCount === 0 ? 0.5 : 1
              }}
            >
              <i className="fas fa-check me-1"></i>
              Retain
            </button>
            <button
              className="btn btn-warning btn-sm"
              onClick={() => handleBulkDecision('Modify')}
              disabled={pendingSelectedCount === 0}
              style={{
                padding: '8px 16px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                fontWeight: '500',
                opacity: pendingSelectedCount === 0 ? 0.5 : 1
              }}
            >
              <i className="fas fa-edit me-1"></i>
              Modify
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleBulkDecision('Removed')}
              disabled={pendingSelectedCount === 0}
              style={{
                padding: '8px 16px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                fontWeight: '500',
                opacity: pendingSelectedCount === 0 ? 0.5 : 1
              }}
            >
              <i className="fas fa-times me-1"></i>
              Remove
            </button>
            <button
              className="btn btn-info btn-sm text-white"
              onClick={() => openUserDelegateModal()}
              disabled={pendingSelectedCount === 0}
              style={{
                padding: '8px 16px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                fontWeight: '500',
                opacity: pendingSelectedCount === 0 ? 0.5 : 1
              }}
            >
              <i className="fas fa-user-tag me-1"></i>
              Delegate
            </button>

            {/* Show warning if attested users selected */}
            {pendingSelectedCount === 0 && selectedUsers.length > 0 && (
              <>
                <div style={{
                  width: '1px',
                  height: '24px',
                  background: 'rgba(255,255,255,0.2)'
                }}></div>
                <button
                  className="btn btn-outline-warning btn-sm"
                  onClick={() => setShowClearAttestationModal(true)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}
                >
                  <i className="fas fa-eraser me-1"></i>
                  Clear Attestations
                </button>
              </>
            )}

            {/* Divider */}
            <div style={{
              width: '1px',
              height: '24px',
              background: 'rgba(255,255,255,0.2)'
            }}></div>

            {/* Save Button - always visible, enabled when there are unsaved decisions */}
            <button
              className="btn btn-sm"
              onClick={handleSaveDecisions}
              disabled={loading || Object.keys(decisions).filter(k => decisions[k].decision).length === 0}
              style={{
                padding: '8px 20px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                fontWeight: '600',
                background: Object.keys(decisions).filter(k => decisions[k].decision).length > 0
                  ? 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)'
                  : '#4a5568',
                border: Object.keys(decisions).filter(k => decisions[k].decision).length > 0
                  ? '2px solid #48bb78'
                  : '1px solid #718096',
                color: '#fff',
                boxShadow: Object.keys(decisions).filter(k => decisions[k].decision).length > 0
                  ? '0 4px 12px rgba(56, 161, 105, 0.5)'
                  : 'none',
                opacity: Object.keys(decisions).filter(k => decisions[k].decision).length === 0 ? 0.5 : 1,
                transform: Object.keys(decisions).filter(k => decisions[k].decision).length > 0
                  ? 'scale(1.05)'
                  : 'scale(1)',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-save me-1"></i>
              Save {Object.keys(decisions).filter(k => decisions[k].decision).length > 0
                ? `(${Object.keys(decisions).filter(k => decisions[k].decision).length})`
                : ''}
            </button>

            {/* Clear Selection Button */}
            <button
              className="btn btn-outline-light btn-sm"
              onClick={() => setSelectedUsers([])}
              style={{
                padding: '8px 16px',
                fontSize: '0.85rem',
                borderRadius: '8px'
              }}
            >
              <i className="fas fa-times me-1"></i>
              Clear Selection
            </button>
          </div>,
          document.body
        );
      })()}

      {/* Clear Attestation Confirmation Modal */}
      {showClearAttestationModal && ReactDOM.createPortal(
        <div
          style={{
            backgroundColor: 'rgba(0,0,0,0.6)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowClearAttestationModal(false)}
        >
          <div
            style={{ margin: 0, maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: '1.5rem', color: '#fff' }}></i>
                </div>
                <h5 style={{ color: '#fff', fontWeight: '600', margin: 0 }}>
                  Already Attested Users Selected
                </h5>
              </div>

              {/* Body */}
              <div style={{ padding: '24px' }}>
                <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 16px 0' }}>
                  The selected user(s) have already been attested. To make changes or delegate, you need to clear the existing attestation decisions first.
                </p>

                {/* Show selected attested users */}
                <div style={{
                  background: '#f7fafc',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '8px', fontWeight: '500' }}>
                    Selected attested users ({selectedUsers.filter(userKey => {
                      const user = users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey);
                      return user && user.attestation_decision && user.attestation_decision !== 'Pending';
                    }).length}):
                  </div>
                  {selectedUsers.map(userKey => {
                    const user = users.find(u => `${u.campaign_id}_${u.database_name}_${u.db_username}` === userKey);
                    if (!user || !user.attestation_decision || user.attestation_decision === 'Pending') return null;
                    return (
                      <div key={userKey} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 0',
                        borderBottom: '1px solid #e2e8f0'
                      }}>
                        <span style={{ fontSize: '0.85rem', color: '#2d3748' }}>{user.db_username}</span>
                        <span className={`badge ${
                          user.attestation_decision === 'Retained' ? 'bg-success' :
                          user.attestation_decision === 'Modify' ? 'bg-warning text-dark' :
                          'bg-danger'
                        }`} style={{ fontSize: '0.7rem' }}>
                          {user.attestation_decision}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div style={{
                  padding: '12px 16px',
                  background: '#fff5f5',
                  borderRadius: '8px',
                  border: '1px solid #feb2b2'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <i className="fas fa-info-circle" style={{ color: '#c53030', marginTop: '2px' }}></i>
                    <span style={{ color: '#742a2a', fontSize: '0.85rem' }}>
                      Clearing attestations will reset them to "Pending" status. You can then make new decisions.
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                background: '#f7fafc'
              }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowClearAttestationModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleClearAttestations}
                  disabled={loading}
                >
                  <i className="fas fa-eraser me-2"></i>
                  Clear Attestations
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Modify Privilege Modal */}
      {showBulkModifyModal && bulkModifyUsers.length > 0 && ReactDOM.createPortal(
        <div
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.7)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}
          onClick={() => {
            setShowBulkModifyModal(false);
            setBulkModifyUsers([]);
            setBulkTargetPrivilegeLevel('');
            setBulkModifyComment('');
          }}
        >
          <div
            style={{
              maxWidth: 700,
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#ffffff',
                borderRadius: 8,
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                border: '2px solid #333',
                overflow: 'hidden'
              }}>
              {/* Header */}
              <div style={{ flexShrink: 0, backgroundColor: '#ffc107', borderTopLeftRadius: 8, borderTopRightRadius: 8, padding: 20, borderBottom: '2px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 style={{ color: '#000', fontWeight: '600', margin: 0, fontSize: '1.25rem' }}>
                  <i className="fas fa-edit me-2"></i>
                  Bulk Modify Privilege Level ({bulkModifyUsers.length} users)
                </h5>
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModifyModal(false);
                    setBulkModifyUsers([]);
                    setBulkTargetPrivilegeLevel('');
                    setBulkModifyComment('');
                  }}
                  aria-label="Close"
                  style={{ background: 'transparent', border: 'none', fontSize: '28px', fontWeight: '700', lineHeight: 1, color: '#000', cursor: 'pointer', padding: '0', opacity: 0.7 }}
                  onMouseOver={(e) => e.target.style.opacity = '1'}
                  onMouseOut={(e) => e.target.style.opacity = '0.7'}
                >×</button>
              </div>

              {/* Body */}
              <div style={{ overflowY: 'auto', flex: '1 1 auto', backgroundColor: '#ffffff', padding: 24 }}>
                {/* Selected Users List */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: '#212529', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                    Selected Users:
                  </label>
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '12px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid #e9ecef'
                  }}>
                    {bulkModifyUsers.map((user, idx) => (
                      <div key={user.userKey} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 8px',
                        background: idx % 2 === 0 ? '#fff' : '#f8f9fa',
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }}>
                        <span style={{ fontSize: '0.9rem', color: '#212529', fontWeight: '500' }}>
                          {user.db_username}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{user.database_name}</span>
                          <span
                            style={{
                              ...getPrivilegeLevelStyle(user.privilege_level),
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '0.7rem'
                            }}
                          >
                            {user.privilege_level || 'Unknown'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Target Privilege Level */}
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="bulkTargetPrivilegeLevel" style={{ color: '#212529', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                    Select Target Privilege Level: <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '8px' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    All selected users are at <strong>{bulkModifyUsers[0]?.privilege_level || 'Unknown'}</strong>. Only lower privilege levels are shown.
                  </p>
                  <select
                    className="form-select"
                    id="bulkTargetPrivilegeLevel"
                    value={bulkTargetPrivilegeLevel}
                    onChange={(e) => setBulkTargetPrivilegeLevel(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '1rem', color: '#212529', backgroundColor: '#fff' }}
                  >
                    <option value="">-- Select Target Privilege Level --</option>
                    {getAvailablePrivilegeLevels(bulkModifyUsers[0]?.privilege_level, bulkModifyUsers[0]?.database_type).map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                {/* Comments */}
                <div style={{ marginBottom: '0' }}>
                  <label htmlFor="bulkModifyComment" style={{ color: '#212529', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                    Comments: <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '8px' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    Comments are mandatory for Modify decisions. Explain why the privilege level should be reduced.
                  </p>
                  <textarea
                    className="form-control"
                    id="bulkModifyComment"
                    rows="4"
                    value={bulkModifyComment}
                    onChange={(e) => setBulkModifyComment(e.target.value)}
                    placeholder="Enter justification for privilege level modification..."
                    style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '1rem', color: '#212529', backgroundColor: '#fff', resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* Footer */}
              <div style={{ flexShrink: 0, padding: '16px 24px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: '#f8f9fa' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowBulkModifyModal(false);
                    setBulkModifyUsers([]);
                    setBulkTargetPrivilegeLevel('');
                    setBulkModifyComment('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  disabled={!bulkTargetPrivilegeLevel || !bulkModifyComment.trim()}
                  onClick={() => {
                    // All selected users share one privilege level (enforced up-front in
                    // handleBulkDecision), and the target dropdown only exposes lower levels,
                    // so every row is applied uniformly.
                    const newDecisions = { ...decisions };
                    bulkModifyUsers.forEach(user => {
                      newDecisions[user.userKey] = {
                        ...newDecisions[user.userKey],
                        decision: 'Modify',
                        targetPrivilegeLevel: bulkTargetPrivilegeLevel,
                        comment: bulkModifyComment.trim()
                      };
                    });

                    const appliedCount = bulkModifyUsers.length;
                    setDecisions(newDecisions);
                    setShowBulkModifyModal(false);
                    setBulkModifyUsers([]);
                    setBulkTargetPrivilegeLevel('');
                    setBulkModifyComment('');

                    setStatusRibbon({
                      tone: 'success',
                      title: `Applied Modify decision to ${appliedCount} user(s)`,
                      details: ['Click Save to submit.'],
                      autoDismissMs: 10000
                    });
                  }}
                >
                  <i className="fas fa-check me-2"></i>
                  Apply Modify Decision
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Mixed Privilege Levels Warning Modal (Bulk Modify) */}
      {showMixedPrivilegeModal && ReactDOM.createPortal(
        <div
          style={{
            backgroundColor: 'rgba(0,0,0,0.6)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowMixedPrivilegeModal(false)}
        >
          <div
            style={{ margin: 0, maxWidth: '520px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #d69e2e 0%, #b7791f 100%)',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: '1.5rem', color: '#fff' }}></i>
                </div>
                <h5 style={{ color: '#fff', fontWeight: '600', margin: 0 }}>
                  Bulk Modify Not Allowed
                </h5>
              </div>

              <div style={{ padding: '24px' }}>
                <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', margin: 0 }}>
                  Bulk modification is only allowed for accounts that share the <strong>same current privilege level</strong>,
                  because the target level must be strictly lower than the current level for every selected account.
                </p>
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  background: '#fffaf0',
                  borderRadius: '8px',
                  border: '1px solid #feebc8'
                }}>
                  <div style={{ color: '#744210', fontSize: '0.9rem', marginBottom: '6px', fontWeight: '600' }}>
                    Your selection includes these privilege levels:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {mixedPrivilegeLevels.map(level => (
                      <span
                        key={level}
                        style={{
                          ...getPrivilegeLevelStyle(level),
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}
                      >
                        {level}
                      </span>
                    ))}
                  </div>
                </div>
                <p style={{ color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.5', marginTop: '16px', marginBottom: 0 }}>
                  Please narrow your selection to accounts at a single privilege level and try again,
                  or modify accounts individually.
                </p>
              </div>

              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                background: '#f7fafc'
              }}>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => setShowMixedPrivilegeModal(false)}
                >
                  <i className="fas fa-check me-2"></i>
                  OK, Got It
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* All Users Attested Notification Modal */}
      {showAllAttestedModal && ReactDOM.createPortal(
        <div
          style={{
            backgroundColor: 'rgba(0,0,0,0.6)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowAllAttestedModal(false)}
        >
          <div
            style={{ margin: 0, maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{
                background: allAttestedModalType === 'closed'
                  ? 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)'
                  : 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i
                    className={`fas ${allAttestedModalType === 'closed' ? 'fa-lock' : 'fa-check-circle'}`}
                    style={{ fontSize: '1.5rem', color: '#fff' }}
                  ></i>
                </div>
                <h5 style={{ color: '#fff', fontWeight: '600', margin: 0 }}>
                  {allAttestedModalType === 'closed' ? 'Campaign Closed' : 'All Users Attested'}
                </h5>
              </div>

              {/* Body */}
              <div style={{ padding: '24px' }}>
                <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', margin: 0 }}>
                  {allAttestedModalMessage}
                </p>

                {allAttestedModalType === 'attested' && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: '#ebf8ff',
                    borderRadius: '8px',
                    border: '1px solid #bee3f8'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="fas fa-lightbulb" style={{ color: '#3182ce' }}></i>
                      <span style={{ color: '#2c5282', fontSize: '0.9rem' }}>
                        Use the <strong>"Attested Users"</strong> or <strong>"All Users"</strong> filter to view your decisions.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                background: '#f7fafc'
              }}>
                <button
                  type="button"
                  className={`btn ${allAttestedModalType === 'closed' ? 'btn-secondary' : 'btn-success'}`}
                  onClick={() => setShowAllAttestedModal(false)}
                >
                  <i className="fas fa-check me-2"></i>
                  OK, Got It
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ManagerAttestations;
